// src/components/MainContent.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Theme,
  Model,
  MessageWithCitations,
  Conversation,
  BillingSource,
  UserTeamSummary,
  isTeamBilled,
  teamUsableCredits,
  teamCreditsBreakdown,
} from '../types';
import ExportDataButton from './ExportDataButton';
import ModelsModal from './ModelsModal';
import Cookies from 'js-cookie';
import { modelCategories, getModelIcon, isModelFree, isModelPremium, isModelLite } from '../config/models';
import { isImageGenerationModel } from './chat/chatUtils';
import ContextStrip from './chat/ContextStrip';
import useKeyboardInset from '../hooks/useKeyboardInset';
import { CircleStackIcon } from '@heroicons/react/24/outline';
import {
  duplicateConversation,
  ConversationVisibility,
  ProjectRow,
  TeamUnauthorizedError,
  PlaybookRow,
  PlaybookRunSummary,
  ProgramRow,
} from './chat/teamService';

interface MainContentProps {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isMobile: boolean;
  fetchConversationMessages: (
    conversationId: number,
    conversationUuid: string,
    cursor?: string | null,
    direction?: 'next' | 'previous',
    pageSize?: number
  ) => Promise<void>;
  themeColors: any;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  selectedModel: Model;
  setSelectedModel: (model: Model) => void;
  // Multi-select compare mode (optional, opt-in from parent)
  selectedModels?: Model[];
  setSelectedModels?: (models: Model[]) => void;
  userInfo?: {
    status: string;
    name: string;
    email: string;
    credits_used: number;
    // Team-aware credit fields from /auth/credit-info (see src/types.ts User).
    credits_total?: number | null;
    billing_source?: BillingSource;
    team?: UserTeamSummary | null;
    raw_status?: string;
    has_team?: boolean;
  } | null;
  isProfileOpen: boolean;
  setIsProfileOpen: (isOpen: boolean) => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  isLoadingConversations: boolean;
  messages: MessageWithCitations[];
  isFirstMessageLoading: boolean;
  isLoadingMessages: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  paginationData: {
    nextCursor: string | null;
    previousCursor: string | null;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  currentConversationId: number | null;
  isSwitchingConversation: boolean;
  messageStyles: any;
  renderMessage: (message: MessageWithCitations) => JSX.Element;
  UserIcon: React.FC;
  AIIcon: React.FC<{ theme: Theme }>;
  updatedLayoutStyles: any;
  isDragging: boolean;
  isPerplexityModel: (model: string) => boolean;
  shouldShowBrowserIcon: (model: string) => boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  inputMessage: string;
  setInputMessage: (message: string) => void;
  handleTextAreaHeight: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  getPlaceholderText: (model: string) => string;
  isLoading: boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  selectedFileName: string | null;
  selectedFile: File | null;
  selectedFiles?: { file: File; filename: string }[];
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setSelectedFile: (file: File | null) => void;
  setSelectedFileName: (fileName: string | null) => void;
  setSelectedFiles?: (files: { file: File; filename: string }[]) => void;
  isFileUploadEnabled: (model: string) => boolean;
  handleNewChat: () => void;
  isLoadingConversation: boolean;
  conversations: Conversation[];
  showRightPanel: boolean;
  handleClipboardPaste: (e: ClipboardEvent) => Promise<void>;
  // Inline composer error for workspace upload failures (403/413/network).
  uploadError?: string | null;
  onDismissUploadError?: () => void;
  // Mode Suara: tombol mic di composer welcome. Absen = tidak dirender.
  onOpenVoice?: () => void;
  // Refreshes /auth/credit-info after the exhausted-credit code succeeds.
  onCreditRedeemed?: () => void | Promise<void>;

  // --- Teams phase 2: shared conversations ---
  // True when the open conversation belongs to another team member
  // (is_owner === false). Suppresses every write affordance: composer,
  // drag/drop, focus stealing, suggestion chips.
  readOnly?: boolean;
  // uuid of the open conversation. Needed because a shared conversation is NOT
  // in `conversations` (that list is personal only), so the duplicate action
  // cannot look it up there.
  conversationUuid?: string | null;
  // Owner of the shared conversation, shown in the read-only bar.
  sharedOwnerEmail?: string | null;
  // Title from the conversation-detail payload. `conversations` only holds the
  // viewer's OWN conversations, so a shared one is never in it and the crumb
  // would otherwise fall through to "Percakapan baru".
  conversationTitle?: string | null;
  // Parent chat this one was branched from. Crumb shows a "dari asal" link.
  branchedFrom?: { uuid: string; title: string | null } | null;
  // Called after a successful duplicate so the parent can refresh its sidebar
  // list; navigation to the copy happens here.
  onDuplicated?: (uuid: string) => void;
  // Share toggle (own conversations, user has a team): shown next to the crumb.
  canShare?: boolean;
  shareVisibility?: ConversationVisibility;
  sharePending?: boolean;
  // False while the conversation's meta is still being fetched: until then
  // `shareVisibility` is only a default, so the pill disables itself and says
  // "Memuat…" rather than offering an action that could flip the wrong way.
  // Absent = ready (older callers keep today's behaviour).
  shareReady?: boolean;
  onToggleShare?: () => void;
  // Public link-share (anyone / password / email OTP). Owner-only, not team-gated.
  canLinkShare?: boolean;
  linkShareActive?: boolean;
  onOpenLinkShare?: () => void;

  // --- Teams phase 3: project assignment ---
  // Same gate as canShare (own conversation + the account has a team): the
  // menu only ever appears on a conversation the viewer can actually move.
  canAssignProject?: boolean;
  projects?: ProjectRow[];
  currentProjectId?: number | null;
  projectPending?: boolean;
  // null = "Tanpa project". The parent owns the API call, because a TEAM
  // project auto-shares the conversation and the share state lives there.
  onSelectProject?: (projectId: number | null) => void;
  // "Does picking this project also share the chat?" - asked by the assign menu
  // so it can put the confirmation INLINE on the row instead of raising a
  // browser dialog from the parent.
  projectNeedsShareConfirm?: (projectId: number) => boolean;

  // --- Nav rework (2026-07-27): one shell, three surfaces ---
  // Which centre column renders. 'team'/'project' swap the thread + composer
  // for the panel below and turn the crumb slot into a top-LEFT back chevron.
  surface?: 'chat' | 'team' | 'project';
  // The panel to render for a non-chat surface. Scalar `panelProjectId` rides
  // along because the memo comparator cannot compare a JSX node.
  panel?: React.ReactNode;
  panelProjectId?: number | null;
  panelTitle?: string | null;
  onBackToChat?: () => void;
  // Where the back chevron actually goes ("Kembali ke Tim" / "Kembali ke chat"),
  // computed by the shell from the surface it popped from.
  backLabel?: string;
  // "New chat inside this project" (route /c?project=:id). Drives the removable
  // Project pill on the welcome screen and the crumb of a brand-new chat.
  pendingProjectId?: number | null;
  onClearPendingProject?: () => void;
  // Create-and-assign in one step from the assign menu's "Project baru…" row.
  // Resolves to the created row (null on failure) so we can assign to it.
  onCreateProject?: (
    name: string,
    visibility?: 'team' | 'private'
  ) => Promise<ProjectRow | null>;

  // --- Teams onboarding: the one-time "welcome to the team" card ---
  // Set only right after an invite join (see chat/teamWelcome.ts). Rendered in
  // the empty-chat hero, which is exactly where a new member lands. Absent =
  // nothing to show, which is the normal case for everyone else.
  teamWelcome?: { teamId: number; teamName: string } | null;
  onDismissTeamWelcome?: () => void;
  hasTeam?: boolean;
  playbooks?: PlaybookRow[];
  onStartPlaybook?: (row: PlaybookRow) => void;
  myPrograms?: ProgramRow[];
  playbookRun?: PlaybookRunSummary | null;
  onCompletePlaybook?: () => void;
  playbookCompletePending?: boolean;
}

// The bespoke hover-tooltip div is gone: the top bar now uses the native
// `title` like every other control in the app, so there is ONE tooltip
// mechanism instead of two (and none of them is English any more).

const calculateTotalCharacters = (messages: MessageWithCitations[]): number => {
  return messages.reduce((total, msg) => total + (msg.content?.length || 0), 0);
};

const MainContent: React.FC<MainContentProps> = React.memo(({
  theme,
  setTheme,
  isMobile,
  fetchConversationMessages,
  themeColors,
  isSidebarOpen,
  setIsSidebarOpen,
  selectedModel,
  setSelectedModel,
  selectedModels,
  setSelectedModels,
  userInfo,
  isProfileOpen,
  setIsProfileOpen,
  dropdownRef,
  isLoadingConversations,
  messages,
  isFirstMessageLoading,
  isLoadingMessages,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  chatContainerRef,
  messagesEndRef,
  paginationData,
  currentConversationId,
  isSwitchingConversation,
  messageStyles,
  renderMessage,
  UserIcon,
  AIIcon,
  updatedLayoutStyles,
  isDragging,
  isPerplexityModel,
  shouldShowBrowserIcon,
  textareaRef,
  inputMessage,
  setInputMessage,
  handleTextAreaHeight,
  handleKeyPress,
  getPlaceholderText,
  isLoading,
  handleSubmit,
  selectedFileName,
  selectedFile,
  selectedFiles,
  handleFileSelect,
  setSelectedFile,
  setSelectedFileName,
  setSelectedFiles,
  isFileUploadEnabled,
  handleNewChat,
  isLoadingConversation,
  conversations,
  showRightPanel,
  handleClipboardPaste,
  uploadError,
  onDismissUploadError,
  onOpenVoice,
  readOnly,
  conversationUuid,
  sharedOwnerEmail,
  conversationTitle,
  branchedFrom,
  onDuplicated,
  canShare,
  shareVisibility,
  sharePending,
  shareReady,
  onToggleShare,
  canLinkShare,
  linkShareActive,
  onOpenLinkShare,
  canAssignProject,
  projects,
  currentProjectId,
  projectPending,
  onSelectProject,
  projectNeedsShareConfirm,
  teamWelcome,
  onDismissTeamWelcome,
  hasTeam,
  playbooks,
  onStartPlaybook,
  myPrograms,
  playbookRun,
  onCompletePlaybook,
  playbookCompletePending,
  surface = 'chat',
  panel,
  panelProjectId,
  panelTitle,
  onBackToChat,
  backLabel = 'Kembali ke chat',
  pendingProjectId,
  onClearPendingProject,
  onCreateProject,
}) => {
  const navigate = useNavigate();
  const isChatSurface = surface === 'chat';
  // Files-only sends are allowed: enable send when text OR an attachment exists.
  const canSend = (inputMessage.trim().length > 0 || (selectedFiles?.length ?? 0) > 0) && !isLoading;
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [modelsModalOpen, setModelsModalOpen] = useState(false);
  // Read-only bar: duplicating someone else's shared conversation into my own.
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  const [isTextareaReady, setIsTextareaReady] = useState(false);
  const [showExpiredWarning, setShowExpiredWarning] = useState(true);
  const [showLimitedWarning, setShowLimitedWarning] = useState(true);
  const [showLiteWarning, setShowLiteWarning] = useState(true);
  const [showGenericWarning, setShowGenericWarning] = useState(true);
  const personalCreditsExhausted = userInfo?.billing_source === 'none' &&
    typeof userInfo.credits_total === 'number' &&
    userInfo.credits_used >= userInfo.credits_total;
  // Project menu (Teams phase 3): anchored dropdown next to the share control.
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState(false);
  const projectMenuRef = useRef<HTMLDivElement>(null);
  // "Project baru…" inside that menu: create AND assign without leaving the bar.
  const [menuCreateOpen, setMenuCreateOpen] = useState(false);
  const [menuCreateName, setMenuCreateName] = useState('');
  const [menuCreateVisibility, setMenuCreateVisibility] = useState<'team' | 'private'>('team');
  const [menuCreating, setMenuCreating] = useState(false);
  // Inline confirms (there is no window.confirm left in the app):
  //  - the project row that would ALSO share the chat flips into the question
  //  - the "Dibagikan · Tim" pill flips into "Yakin privat?" / "Batal"
  const [confirmAssignProjectId, setConfirmAssignProjectId] = useState<number | null>(null);
  const [confirmUnshare, setConfirmUnshare] = useState(false);
  // Only a team account has a scope choice; a team-less one only ever makes
  // personal projects, so the control would be a lie.
  const canScopeNewProject = !!userInfo?.team;

  // One pill geometry for the whole top bar: AUTO, Project, Dibagikan and the
  // team chip were 26/26/26 tall but 10 / 11.5 / 11.5 / 10.5px, which read as
  // four sizes of the same component. The avatar stays 30px on purpose.
  const PILL_H = '26px';
  const PILL_FS = '11px';
  // Closing the menu must also drop any half-answered confirm, or reopening it
  // shows a question the user already walked away from.
  const closeProjectMenu = () => {
    setIsProjectMenuOpen(false);
    setConfirmAssignProjectId(null);
  };

  const submitMenuCreate = async () => {
    const name = menuCreateName.trim();
    if (!name || menuCreating || !onCreateProject) return;
    setMenuCreating(true);
    try {
      const created = await onCreateProject(
        name,
        canScopeNewProject ? menuCreateVisibility : undefined
      );
      if (created?.id != null) {
        setMenuCreateOpen(false);
        setMenuCreateName('');
        setIsProjectMenuOpen(false);
        // One step: the conversation lands in the project that was just made.
        onSelectProject?.(created.id);
      }
    } finally {
      setMenuCreating(false);
    }
  };

  // Close the project menu on an outside click and whenever the open
  // conversation changes (its assignment no longer applies).
  useEffect(() => {
    if (!isProjectMenuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (projectMenuRef.current && !projectMenuRef.current.contains(event.target as Node)) {
        setIsProjectMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isProjectMenuOpen]);

  useEffect(() => {
    setIsProjectMenuOpen(false);
  }, [currentConversationId]);

  // iOS keyboard: drives the welcome composer's bottom-padding swap. The
  // shell-follow (--app-y) owns all positional compensation now.
  const { inset: keyboardInset } = useKeyboardInset(isMobile);

  // Closing the menu also drops a half-typed new project name.
  useEffect(() => {
    if (!isProjectMenuOpen) {
      setMenuCreateOpen(false);
      setMenuCreateName('');
    }
  }, [isProjectMenuOpen]);

  // Add effect to focus textarea when entering empty screen
  // (never in a read-only shared view - there is no composer to focus, and the
  // query would otherwise grab an unrelated textarea)
  useEffect(() => {
    if (readOnly) return;
    if (messages.length === 0) {
      const bottomTextarea = document.querySelector('textarea');
      if (bottomTextarea) {
        bottomTextarea.focus();
        // Force cursor to be visible and active
        bottomTextarea.style.caretColor = theme === 'light' ? '#000000' : '#FFFFFF';
        bottomTextarea.style.cursor = 'text';
        // Set cursor position to end
        const length = bottomTextarea.value.length;
        bottomTextarea.setSelectionRange(length, length);
      }
    }
  }, [messages.length, theme, readOnly]);

  // Add effect to focus textarea when model changes
  useEffect(() => {
    if (readOnly) return;
    if (textareaRef.current && messages.length === 0) {
      // Try to focus immediately
      textareaRef.current.focus();
      
      // If immediate focus fails, try again after a short delay
      if (document.activeElement !== textareaRef.current) {
        const focusInterval = setInterval(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            if (document.activeElement === textareaRef.current) {
              clearInterval(focusInterval);
            }
          }
        }, 100);

        // Clear interval after 1 second to prevent infinite attempts
        setTimeout(() => clearInterval(focusInterval), 1000);
      }
    }
  }, [selectedModel, messages.length, isTextareaReady, readOnly]);

  // Add effect to check if textarea is ready
  useEffect(() => {
    if (textareaRef.current) {
      setIsTextareaReady(true);
    }
  }, [textareaRef.current]);

  // Add useEffect to attach paste event listener
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('paste', handleClipboardPaste);
      
      return () => {
        textarea.removeEventListener('paste', handleClipboardPaste);
      };
    }
  }, [textareaRef, handleClipboardPaste]);

  // Open the popup when the trial is spent ('verified' without capacity is the
  // only state the backend still reports as verified). Scroll locking lives in
  // the popup's own effect so closing always restores it.
  useEffect(() => {
    if (userInfo?.status === 'verified') {
      setShowSubscriptionPopup(true);
    }
  }, [userInfo]);

  // Subscription popup component
  // Subscription popup: shown when the trial allowance is spent (effective
  // status 'verified' without capacity). Rewritten 2026-08-02 for pricing v3;
  // the old version was frozen in the model-picker era (tutorial video, model
  // grid, per-model credit rules) and had NO close affordance, trapping users.
  // Now closeable via X, backdrop, Escape, and "Nanti dulu" - being able to
  // reread old chats without paying is intentional.
  useEffect(() => {
    document.body.style.overflow = showSubscriptionPopup ? 'hidden' : '';
    if (!showSubscriptionPopup) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowSubscriptionPopup(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [showSubscriptionPopup]);

  const SubscriptionPopup = () => {
    if (!showSubscriptionPopup) return null;
    const ink = theme === 'light' ? '#1F2937' : '#E5E7EB';
    const sub = theme === 'light' ? '#4B5563' : '#9CA3AF';
    const card = theme === 'light' ? '#F3F4F6' : '#2D2D2D';
    return (
      <div
        onClick={() => setShowSubscriptionPopup(false)}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000, padding: '20px',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: theme === 'light' ? 'white' : '#202123',
            padding: '2rem', borderRadius: '0.75rem', width: '100%',
            maxWidth: isMobile ? '92%' : '520px', position: 'relative',
            maxHeight: '85vh', overflowY: 'auto',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          <button
            onClick={() => setShowSubscriptionPopup(false)}
            aria-label="Tutup"
            style={{
              position: 'absolute', top: '14px', right: '14px',
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: sub, padding: '6px',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', margin: 0, color: ink }}>
            Kredit gratis kamu sudah terpakai
          </h2>
          <p style={{ color: sub, margin: '0.75rem 0 1.5rem' }}>
            Senang kamu sudah mencoba Power AI. Untuk lanjut memakai riset web,
            baca file, bikin gambar dan presentasi, pilih salah satu plan:
          </p>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div style={{ flex: 1, background: card, borderRadius: '0.5rem', padding: '1rem', border: `2px solid ${theme === 'light' ? '#2563EB' : '#3B82F6'}` }}>
              <div style={{ fontWeight: 700, color: ink }}>Individual</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: theme === 'light' ? '#2563EB' : '#3B82F6' }}>
                Rp199.000<span style={{ fontSize: '0.85rem', fontWeight: 400 }}>/bulan</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: sub }}>10.000 kredit tiap bulan, semua fitur terbuka</div>
            </div>
            <div style={{ flex: 1, background: card, borderRadius: '0.5rem', padding: '1rem' }}>
              <div style={{ fontWeight: 700, color: ink }}>Team</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: ink }}>
                Rp399.000<span style={{ fontSize: '0.85rem', fontWeight: 400 }}>/bulan</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: sub }}>25.000 kredit dibagi bareng, sampai 4 orang</div>
            </div>
          </div>

          <button
            onClick={() => navigate('/subscription')}
            style={{
              width: '100%', padding: '0.8rem', borderRadius: '0.5rem',
              border: 'none', cursor: 'pointer', fontWeight: 600,
              background: theme === 'light' ? '#2563EB' : '#3B82F6', color: 'white',
              fontSize: '0.95rem',
            }}
          >
            Lihat Plan & Berlangganan
          </button>
          <button
            onClick={() => setShowSubscriptionPopup(false)}
            style={{
              width: '100%', padding: '0.7rem', marginTop: '0.5rem',
              borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
              background: 'transparent', color: sub, fontSize: '0.9rem',
            }}
          >
            Nanti dulu
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .model-card {
        transition: transform 0.2s ease, box-shadow 0.2s ease;
      }
      .model-card:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 6px ${theme === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.2)'};
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
      return undefined;
    };
  }, [theme]);

  const truncateFilename = (filename: string, maxLength: number) => {
    if (filename.length <= maxLength) return filename;
    const extension = filename.slice(filename.lastIndexOf('.'));
    const name = filename.slice(0, filename.lastIndexOf('.'));
    return name.slice(0, maxLength - extension.length - 3) + '...' + extension;
  };

  // Update the model selection handler. In compare mode (selectedModels +
  // setSelectedModels provided), this toggles the model in/out of the
  // selected array (capped at 3); otherwise single-select behaves as
  // before.
  const handleModelSelect = (model: Model) => {
    // For expired users - block all model access
    if (userInfo?.status === 'expired') {
      const expiredMessage = "Langganan Anda telah berakhir. Silakan perpanjang langganan untuk mengakses model AI apapun.";
      alert(expiredMessage);
      return;
    }

    // For limited users - only allow free models
    if (userInfo?.status === 'limited' && !isModelFree(model)) {
      const limitedMessage = "Akses terbatas: Anda hanya dapat menggunakan model gratis. Silakan upgrade langganan untuk mengakses model premium.";
      alert(limitedMessage);
      return;
    }

    // For lite users - only allow lite models
    if (userInfo?.status === 'lite' && !isModelLite(model)) {
      const upgradeMessage = "Ini adalah model PRO. Silakan upgrade langganan Anda untuk mengakses model premium.";
      alert(upgradeMessage);
      return;
    }

    // Compare-mode toggle on the empty-screen grid
    if (selectedModels && setSelectedModels) {
      // Image-gen models are always single. Picking one (or while one is
      // active) collapses selection to just that model.
      const isImage = isImageGenerationModel(model);
      const hasImageSelected = selectedModels.some((m) => isImageGenerationModel(m));
      if (isImage || hasImageSelected) {
        setSelectedModels([model]);
        setSelectedModel(model);
        return;
      }

      const picked = selectedModels.includes(model);
      if (picked) {
        if (selectedModels.length > 1) {
          setSelectedModels(selectedModels.filter((m) => m !== model));
        }
      } else if (selectedModels.length < 3) {
        setSelectedModels([...selectedModels, model]);
      }
      return;
    }

    setSelectedModel(model);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Read-only shared view: dropping a file must do nothing at all (the drop
  // would otherwise queue an upload for a conversation we cannot write to).
  const noopDrag = (e: React.DragEvent) => { e.preventDefault(); };

  // Read-only bar action: copy the shared conversation into my own space and
  // open the copy. Files and runs are not copied (backend contract).
  const handleDuplicate = async () => {
    if (!conversationUuid || duplicating) return;
    setDuplicating(true);
    setDuplicateError('');
    try {
      const copy = await duplicateConversation(conversationUuid);
      if (copy?.uuid) {
        onDuplicated?.(copy.uuid);
        navigate(`/c/${copy.uuid}`);
      } else {
        setDuplicateError('Gagal menduplikat. Coba lagi.');
      }
    } catch (err) {
      if (err instanceof TeamUnauthorizedError) {
        navigate('/login');
        return;
      }
      console.error('duplicate conversation failed:', err);
      setDuplicateError('Gagal menduplikat. Coba lagi.');
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div
    style={updatedLayoutStyles.mainContent}
    onDragOver={readOnly ? noopDrag : handleDragOver}
    onDragLeave={readOnly ? noopDrag : handleDragLeave}
    onDrop={readOnly ? noopDrag : handleDrop}
  >
    {/* Add subscription popup */}
    <SubscriptionPopup />
    
    {/* No per-navbar keyboard compensation: the whole mobile shell follows
        the visual viewport now (--app-y in useTheme wrapper); translating the
        navbar again double-shifted it during the keyboard animation. */}
    <div style={updatedLayoutStyles.navbar}>
      {/* The collapsed desktop state used to repeat the rail's own "Buka
          sidebar" and "Chat baru" here as two unlabelled 36x36 glyphs. The rail
          renders under exactly the same condition (!isMobile && !isSidebarOpen)
          and labels both, so this pair was pure duplication and is gone. */}

      {/* Mobile burger menu */}
      {isMobile && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          title={isSidebarOpen ? 'Tutup sidebar' : 'Buka sidebar'}
          aria-label={isSidebarOpen ? 'Tutup sidebar' : 'Buka sidebar'}
          className="pa-icon-ghost"
          style={{
            background: 'none',
            border: 'none',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            padding: 0,
            cursor: 'pointer',
            color: themeColors[theme].text,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            marginRight: '4px',
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M15 3v18" />
          </svg>
        </button>
      )}

      {/* New Chat button (mobile only, shown when sidebar is closed) */}
      {isMobile && !isSidebarOpen && (
        <button
          onClick={handleNewChat}
          title="Chat baru"
          aria-label="Chat baru"
          className="pa-icon-ghost"
          style={{
            background: 'none',
            border: 'none',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            padding: 0,
            cursor: 'pointer',
            color: themeColors[theme].text,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            marginRight: '6px',
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </button>
      )}

      {/* Panel surfaces (team / project): the crumb slot becomes a top-LEFT
          back chevron plus the panel name. That is the ONE way out of a panel,
          which is why "Kembali ke chat" could be deleted from both of them. */}
      {!isChatSurface && (
        <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: '1 1 auto',
          minWidth: 0,
        }}>
          <button
            type="button"
            onClick={() => onBackToChat?.()}
            // The label follows the ACTUAL target: the chevron pops the surface
            // stack, so from a project opened out of /team it says "Kembali ke
            // Tim" and goes there.
            title={backLabel}
            aria-label={backLabel}
            className="pa-icon-ghost"
            style={{
              display: 'grid',
              placeItems: 'center',
              width: '30px',
              height: '30px',
              borderRadius: '8px',
              background: 'transparent',
              border: `1px solid ${themeColors[theme].line2}`,
              color: themeColors[theme].ink2,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <span style={{
            fontWeight: 600,
            fontSize: '13.5px',
            color: themeColors[theme].ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: isMobile ? '160px' : '460px',
          }}>
            {panelTitle || (surface === 'team' ? 'Tim' : 'Project')}
          </span>
        </span>
      )}

      {/* Conversation crumb: live jade dot + optional project segment + title
          (fallback) + AUTO pill. Every request routes through the backend
          'auto' router. */}
      {isChatSurface && (() => {
        // A conversation filed in a project reads "{Project} > {Chat}", and the
        // project segment is a link back to its panel. A brand-new chat opened
        // from a project has no title yet: it says "Project · {name}" instead of
        // inheriting the previous conversation's title (P1-4).
        const listTitle = conversations.find((cv) => cv.id === currentConversationId)?.title;
        const isNewChat = currentConversationId == null;
        const contextProjectId = isNewChat ? (pendingProjectId ?? null) : (currentProjectId ?? null);
        const crumbProject = (projects || []).find((p) => p.id === contextProjectId) || null;
        // "Chat baru", not "Percakapan baru": the product says chat everywhere
        // else (the sidebar button, the rail, the project panel), so the crumb
        // was the only place still using the old word.
        const title = isNewChat
          ? (crumbProject ? `Project · ${crumbProject.name}` : 'Chat baru')
          : (listTitle || conversationTitle || 'Chat baru');
        return (
        // overflow:hidden is what stops the crumb pushing the pills and the
        // avatar off the bar at 390px. Inside it the PROJECT segment is the one
        // allowed to shrink (flex 0 1 auto + minWidth 0) and the chat title
        // keeps a real floor on desktop, so it can no longer be starved down to
        // a couple of characters by a long project name.
        <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          fontWeight: 500,
          fontSize: '13px',
          color: themeColors[theme].ink2,
          userSelect: 'none',
        }}>
          <span style={{
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            background: themeColors[theme].jade,
            boxShadow: '0 0 8px rgba(91,214,169,0.8)',
            flexShrink: 0,
          }} />
          {crumbProject && !isNewChat && (
            <>
              <a
                href={`/p/${crumbProject.id}`}
                onClick={(e) => {
                  // Plain click stays in the SPA; cmd/ctrl/middle click keeps
                  // the browser's own behaviour (that is why this is an <a>).
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  navigate(`/p/${crumbProject.id}`);
                }}
                title={`Buka project ${crumbProject.name}`}
                style={{
                  color: themeColors[theme].ink3,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: '0 1 auto',
                  minWidth: 0,
                  maxWidth: isMobile ? '90px' : '180px',
                }}
              >
                {crumbProject.name}
              </a>
              <span style={{ color: themeColors[theme].ink3, flexShrink: 0 }}>›</span>
            </>
          )}
          <span style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: '1 1 auto',
            minWidth: isMobile ? 0 : '220px',
            maxWidth: isMobile ? '150px' : '440px',
          }}>
            {title}
          </span>
          {branchedFrom && !isNewChat && (
            <a
              href={`/c/${branchedFrom.uuid}`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate(`/c/${branchedFrom.uuid}`);
              }}
              title={branchedFrom.title ? `Dari ${branchedFrom.title}` : 'Kembali ke chat asal'}
              style={{
                color: themeColors[theme].ink3,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              dari asal
            </a>
          )}
          {/* The AUTO pill is gone entirely (2026-08-02, Evan): with the model
              picker removed there is no "non-auto" for it to contrast against,
              so it read as an unexplained mystery label. */}
        </span>
        );
      })()}

      {/* Project menu (Teams phase 3) - ghost pill next to the share control,
          own conversations only. Shows the current assignment inline
          ("Project · {nama}", truncated) and opens an anchored list of every
          project the viewer can see plus "Tanpa project". */}
      {isChatSurface && !isMobile && canAssignProject && onSelectProject && (() => {
        const assigned = (projects || []).find((p) => p.id === currentProjectId) || null;
        // The pill no longer repeats the project NAME: the crumb to its left
        // already carries it, and duplicating it there is what starved the chat
        // title of horizontal room. Unassigned still reads "+ Project" - a bare
        // noun looked like a label for something already set.
        const label = assigned ? 'Project' : '+ Project';
        const title = assigned
          ? `Chat ini ada di project ${assigned.name}. Klik untuk memindahkan.`
          : 'Masukkan chat ini ke project';
        return (
          <div style={{ position: 'relative', flexShrink: 0, marginRight: '4px' }} ref={projectMenuRef}>
            <button
              onClick={() => { if (projectPending) return; if (isProjectMenuOpen) closeProjectMenu(); else setIsProjectMenuOpen(true); }}
              disabled={!!projectPending}
              title={title}
              aria-label={title}
              aria-haspopup="menu"
              aria-expanded={isProjectMenuOpen}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: PILL_H,
                padding: '0 10px',
                borderRadius: '99px',
                fontSize: PILL_FS,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                cursor: projectPending ? 'progress' : 'pointer',
                opacity: projectPending ? 0.6 : 1,
                background: 'transparent',
                border: `1px solid ${assigned ? themeColors[theme].line2 : themeColors[theme].line2}`,
                color: assigned ? themeColors[theme].ink2 : themeColors[theme].ink3,
                transition: 'background 0.15s ease, color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (projectPending) return;
                e.currentTarget.style.color = themeColors[theme].ink2;
                e.currentTarget.style.background = themeColors[theme].panel;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = assigned ? themeColors[theme].ink2 : themeColors[theme].ink3;
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              </svg>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </button>

            {isProjectMenuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  minWidth: '220px',
                  maxWidth: '280px',
                  maxHeight: '320px',
                  overflowY: 'auto',
                  backgroundColor: themeColors[theme].background,
                  border: `1px solid ${themeColors[theme].border}`,
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.18)',
                  padding: '6px',
                  zIndex: 50,
                }}
              >
                {(projects || []).map((p) => {
                  const active = p.id === currentProjectId;
                  // Moving into a TEAM project also shares the chat. That used
                  // to be a window.confirm; the row itself now asks.
                  const confirming = confirmAssignProjectId === p.id;
                  if (confirming) {
                    return (
                      <div
                        key={`menu-proj-${p.id}`}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '8px',
                          background: themeColors[theme].surface2,
                        }}
                      >
                        <div style={{
                          fontSize: '11.5px',
                          lineHeight: 1.5,
                          color: themeColors[theme].secondaryText,
                          marginBottom: '8px',
                        }}>
                          Memindahkan ke <b style={{ color: themeColors[theme].text }}>{p.name}</b> akan membagikan chat ini ke tim.
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            role="menuitem"
                            autoFocus
                            onClick={() => { closeProjectMenu(); onSelectProject(p.id); }}
                            style={{
                              flex: 1,
                              padding: '6px 8px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              fontFamily: 'inherit',
                              cursor: 'pointer',
                              background: 'none',
                              border: `1px solid ${themeColors[theme].jade}`,
                              color: themeColors[theme].jade,
                            }}
                          >
                            Yakin pindahkan?
                          </button>
                          <button
                            onClick={() => setConfirmAssignProjectId(null)}
                            style={{
                              flex: 1,
                              padding: '6px 8px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 500,
                              fontFamily: 'inherit',
                              cursor: 'pointer',
                              background: 'none',
                              border: `1px solid ${themeColors[theme].border}`,
                              color: themeColors[theme].secondaryText,
                            }}
                          >
                            Batal
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={`menu-proj-${p.id}`}
                      role="menuitem"
                      onClick={() => {
                        if (active) { closeProjectMenu(); return; }
                        if (projectNeedsShareConfirm?.(p.id)) {
                          setConfirmAssignProjectId(p.id);
                          return;
                        }
                        closeProjectMenu();
                        onSelectProject(p.id);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        textAlign: 'left',
                        background: active ? themeColors[theme].surface2 : 'none',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 10px',
                        minHeight: '36px',
                        fontSize: '12.5px',
                        fontFamily: 'inherit',
                        color: themeColors[theme].text,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = themeColors[theme].surface2;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = active ? themeColors[theme].surface2 : 'none';
                      }}
                    >
                      {/* Check mark on the current assignment: the row tint
                          alone was not a readable "you are here". */}
                      <span style={{
                        width: '13px',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        color: themeColors[theme].jade,
                      }}>
                        {active && (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </span>
                      <span style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {p.name}
                      </span>
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        // jadeStrong: at 10px uppercase the light-theme jade
                        // does not clear AA on this surface.
                        color: p.visibility === 'private'
                          ? themeColors[theme].mutedText
                          : themeColors[theme].jadeStrong,
                        flexShrink: 0,
                      }}>
                        {p.visibility === 'private' ? 'Privat' : 'Tim'}
                      </span>
                    </button>
                  );
                })}

                {/* "Project baru…" - create AND assign in one step. This row
                    replaces the old dead-end empty-state string ("buat project
                    dulu di halaman Tim"), which named a place instead of doing
                    the thing. */}
                {onCreateProject && (
                  <div style={{
                    borderTop: (projects || []).length > 0
                      ? `1px solid ${themeColors[theme].border}`
                      : 'none',
                    marginTop: (projects || []).length > 0 ? '4px' : 0,
                    paddingTop: (projects || []).length > 0 ? '4px' : 0,
                  }}>
                    {menuCreateOpen ? (
                      <div style={{ padding: '6px 6px 8px' }}>
                        <input
                          autoFocus
                          value={menuCreateName}
                          onChange={(e) => setMenuCreateName(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') void submitMenuCreate();
                            else if (e.key === 'Escape') {
                              setMenuCreateOpen(false);
                              setMenuCreateName('');
                            }
                          }}
                          placeholder="Nama project"
                          aria-label="Nama project"
                          disabled={menuCreating}
                          className="pa-field"
                          style={{
                            width: '100%',
                            background: themeColors[theme].surface2,
                            border: `1px solid ${themeColors[theme].borderStrong}`,
                            borderRadius: '8px',
                            padding: '7px 9px',
                            fontSize: '12.5px',
                            fontFamily: 'inherit',
                            color: themeColors[theme].text,
                            outline: 'none',
                          }}
                        />
                        {/* Team accounts choose the scope right here; a
                            team-less account only has personal projects. */}
                        {canScopeNewProject && (
                          <div style={{ display: 'flex', gap: '4px', marginTop: '7px' }}>
                            {(['team', 'private'] as const).map((v) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setMenuCreateVisibility(v)}
                                style={{
                                  flex: 1,
                                  padding: '5px 8px',
                                  borderRadius: '7px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  fontFamily: 'inherit',
                                  cursor: 'pointer',
                                  background: menuCreateVisibility === v
                                    ? themeColors[theme].surface2
                                    : 'transparent',
                                  border: `1px solid ${menuCreateVisibility === v
                                    ? themeColors[theme].jade
                                    : themeColors[theme].border}`,
                                  color: menuCreateVisibility === v
                                    ? themeColors[theme].text
                                    : themeColors[theme].secondaryText,
                                }}
                              >
                                {v === 'team' ? 'Tim' : 'Privat'}
                              </button>
                            ))}
                          </div>
                        )}
                        <div style={{
                          marginTop: '7px',
                          fontSize: '10.5px',
                          color: themeColors[theme].mutedText,
                        }}>
                          {menuCreating ? 'Membuat…' : 'Enter untuk buat, Esc batal'}
                        </div>
                      </div>
                    ) : (
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuCreateName('');
                          setMenuCreateOpen(true);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 10px',
                          fontSize: '12.5px',
                          fontFamily: 'inherit',
                          color: themeColors[theme].text,
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = themeColors[theme].surface2;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'none';
                        }}
                      >
                        <span style={{ width: '13px', flexShrink: 0, display: 'grid', placeItems: 'center', color: themeColors[theme].ink3 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        </span>
                        Project baru…
                      </button>
                    )}
                  </div>
                )}

                {/* "Tanpa project" is pointless when there is nothing to detach
                    from and nothing to pick, so it hides on the empty state. */}
                {((projects || []).length > 0 || currentProjectId != null) && (
                <button
                  role="menuitem"
                  onClick={() => {
                    closeProjectMenu();
                    if (currentProjectId != null) onSelectProject(null);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: currentProjectId == null ? themeColors[theme].surface2 : 'none',
                    border: 'none',
                    borderTop: `1px solid ${themeColors[theme].border}`,
                    borderRadius: '8px',
                    marginTop: '4px',
                    padding: '8px 10px',
                    fontSize: '12.5px',
                    fontFamily: 'inherit',
                    color: themeColors[theme].secondaryText,
                    cursor: 'pointer',
                  }}
                >
                  Tanpa project
                </button>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Public link-share: owner of a loaded conversation, team or not. */}
      {isChatSurface && canLinkShare && onOpenLinkShare && (
        <button
          onClick={onOpenLinkShare}
          title={linkShareActive ? 'Tautan berbagi aktif. Klik untuk mengatur.' : 'Bagikan percakapan ini lewat tautan'}
          aria-label={linkShareActive ? 'Tautan berbagi aktif. Klik untuk mengatur.' : 'Bagikan percakapan ini lewat tautan'}
          className={isMobile ? 'pa-touch-36' : undefined}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flexShrink: 0,
            marginRight: '4px',
            height: isMobile ? '36px' : PILL_H,
            minWidth: isMobile ? '36px' : undefined,
            padding: isMobile ? '0 8px' : '0 10px',
            borderRadius: '99px',
            fontSize: PILL_FS,
            fontWeight: 500,
            cursor: 'pointer',
            background: linkShareActive ? themeColors[theme].jadeDim : 'transparent',
            border: `1px solid ${linkShareActive ? themeColors[theme].jade : themeColors[theme].line2}`,
            color: linkShareActive ? themeColors[theme].jade : themeColors[theme].ink3,
            transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = linkShareActive ? themeColors[theme].jade : themeColors[theme].ink2;
            e.currentTarget.style.background = linkShareActive ? themeColors[theme].jadeDim : themeColors[theme].panel;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = linkShareActive ? themeColors[theme].jade : themeColors[theme].ink3;
            e.currentTarget.style.background = linkShareActive ? themeColors[theme].jadeDim : 'transparent';
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <span>{linkShareActive ? 'Tautan aktif' : 'Tautan'}</span>
        </button>
      )}

      {/* Share toggle (Teams phase 2) - only for a loaded conversation the
          viewer OWNS, and only when the account actually has a team. Two
          states: ghost "Bagikan ke tim" and the jade "Dibagikan · Tim" pill,
          which asks for confirmation before going private again. */}
      {isChatSurface && canShare && onToggleShare && (() => {
        const shared = shareVisibility === 'team';
        // Not ready = the meta has not answered yet, so we do not KNOW whether
        // this conversation is shared. Disabled + "Memuat…" beats a control
        // that silently does nothing (or the wrong thing) when clicked.
        const notReady = shareReady === false;
        const busy = !!sharePending || notReady;
        const label = notReady
          ? 'Memuat…'
          : shared ? 'Dibagikan · Tim' : 'Bagikan ke tim';
        const title = notReady
          ? 'Memuat…'
          : sharePending
            ? 'Menyimpan perubahan berbagi…'
            : shared
              ? 'Dibagikan ke tim. Klik untuk menjadikan privat.'
              : 'Bagikan percakapan ini ke tim';
        // Going private again used to raise a window.confirm from the shell.
        // The pill asks for itself: one tap arms it, the pair answers.
        if (shared && !notReady && confirmUnshare) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0, marginRight: '4px' }}>
              <button
                onClick={() => { setConfirmUnshare(false); onToggleShare(); }}
                autoFocus
                title="Jadikan privat. Anggota tim tidak bisa membukanya lagi."
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: PILL_H,
                  padding: '0 10px',
                  borderRadius: '99px',
                  fontSize: PILL_FS,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  background: 'transparent',
                  border: `1px solid ${themeColors[theme].line2}`,
                  color: theme === 'light' ? '#DC2626' : '#F87171',
                  fontFamily: 'inherit',
                }}
              >
                Yakin privat?
              </button>
              <button
                onClick={() => setConfirmUnshare(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: PILL_H,
                  padding: '0 10px',
                  borderRadius: '99px',
                  fontSize: PILL_FS,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  background: 'transparent',
                  border: `1px solid ${themeColors[theme].line2}`,
                  color: themeColors[theme].ink2,
                  fontFamily: 'inherit',
                }}
              >
                Batal
              </button>
            </span>
          );
        }
        return (
          <button
            onClick={() => {
              if (busy) return;
              // Sharing is reversible and additive - no question asked. Going
              // BACK to private takes something away from the team, so it does.
              if (shared) setConfirmUnshare(true);
              else onToggleShare();
            }}
            disabled={busy}
            title={title}
            aria-label={title}
            aria-busy={busy}
            className={isMobile ? 'pa-touch-36' : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              flexShrink: 0,
              marginRight: '4px',
              height: isMobile ? '36px' : PILL_H,
              minWidth: isMobile ? '36px' : undefined,
              padding: isMobile ? '0 8px' : '0 10px',
              borderRadius: '99px',
              fontSize: PILL_FS,
              fontWeight: 500,
              cursor: sharePending ? 'progress' : notReady ? 'default' : 'pointer',
              opacity: busy ? 0.55 : 1,
              background: shared && !notReady ? themeColors[theme].jadeDim : 'transparent',
              border: `1px solid ${shared && !notReady ? themeColors[theme].jade : themeColors[theme].line2}`,
              color: shared && !notReady ? themeColors[theme].jade : themeColors[theme].ink3,
              transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (busy) return;
              e.currentTarget.style.color = shared ? themeColors[theme].jade : themeColors[theme].ink2;
              e.currentTarget.style.borderColor = shared ? themeColors[theme].jade : themeColors[theme].line2;
              if (!shared) e.currentTarget.style.background = themeColors[theme].panel;
            }}
            onMouseLeave={(e) => {
              if (busy) return;
              e.currentTarget.style.color = shared ? themeColors[theme].jade : themeColors[theme].ink3;
              e.currentTarget.style.background = shared ? themeColors[theme].jadeDim : 'transparent';
            }}
          >
            {busy ? (
              /* One spinner for both waits (meta loading, PATCH in flight):
                 the pill always says something is happening. */
              <span style={{
                width: '11px',
                height: '11px',
                border: `2px solid ${themeColors[theme].line2}`,
                borderTopColor: themeColors[theme].jade,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                flexShrink: 0,
              }} />
            ) : shared ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
              </svg>
            )}
            {/* Mobile keeps the icon only; the title attribute carries the copy. */}
            {!isMobile && <span>{label}</span>}
          </button>
        );
      })()}

      {/* Profile Section */}
      <div style={{ 
        position: 'relative',
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }} ref={dropdownRef}>
        {/* Credits (desktop): mono "used / total", or the usable TEAM credits
            when a team wallet is what pays (jatah bulanan + kantong top up, one
            number; the title splits it). Totals come from /auth/credit-info
            (credits_total); the 10000 fallback (pricing v3 USER_LIMITS['active'])
            only covers a backend that has not shipped the field yet. */}
        {!isMobile && (() => {
          const legacyTotal = userInfo?.status === 'lite'
            ? 0
            : (userInfo?.status === 'active' || userInfo?.status === 'limited' ? 10000 : 0);
          const totalCredits = userInfo?.billing_source === 'none'
            ? 0
            : userInfo?.billing_source === 'personal'
              ? (userInfo?.credits_total ?? 10000)
              : legacyTotal;
          const used = userInfo?.credits_used ?? 0;

          if (isTeamBilled(userInfo?.billing_source)) {
            const teamCredits = teamUsableCredits(userInfo?.team);
            const teamName = userInfo?.team?.name || '';
            return (
              // A real button: the pool figure and the team name looked like an
              // entry to team management and were inert (P1-1). Now they are.
              <button
                type="button"
                onClick={() => navigate('/team')}
                title={`Kredit tim: ${teamCreditsBreakdown(userInfo?.team)}`}
                aria-label="Kredit dan anggota tim"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                  fontSize: '11.5px',
                  fontWeight: 500,
                  color: themeColors[theme].ink3,
                  background: 'none',
                  border: 'none',
                  padding: '4px 2px',
                  cursor: 'pointer',
                }}
              >
                <span>
                  <b style={{
                    fontFamily: themeColors[theme].mono,
                    fontVariantNumeric: 'tabular-nums',
                    color: themeColors[theme].ink2,
                    fontWeight: 500,
                  }}>
                    {teamCredits.toLocaleString('id-ID')}
                  </b>
                  {' kredit'}
                </span>
                {/* Mono is for numbers only: the team NAME is prose and reads
                    as Inter, like every other label in the top bar. */}
                {teamName && (
                  <span style={{
                    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                    fontSize: PILL_FS,
                    letterSpacing: '0.01em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: PILL_H,
                    padding: '0 9px',
                    borderRadius: '99px',
                    border: `1px solid ${themeColors[theme].line2}`,
                    maxWidth: '140px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>Tim {teamName}</span>
                )}
              </button>
            );
          }

          return (
            <span
              title={`${used} / ${totalCredits} kredit terpakai`}
              style={{
                fontFamily: themeColors[theme].mono,
                fontSize: '11.5px',
                fontWeight: 500,
                color: themeColors[theme].ink3,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <b style={{ color: themeColors[theme].ink2, fontWeight: 500 }}>
                {used.toLocaleString('id-ID')}
              </b>
              {' / '}{totalCredits.toLocaleString('id-ID')}
            </span>
          );
        })()}

        {/* Profile - avatar square (hairline ring). It was the only control in
            the bar with no title and no accessible name at all: a single
            letter is not a label. */}
        <button
          onClick={() => setIsProfileOpen(!isProfileOpen)}
          title="Akun"
          aria-label="Akun"
          aria-haspopup="menu"
          aria-expanded={isProfileOpen}
          style={{
            background: themeColors[theme].panel2,
            border: `1px solid ${themeColors[theme].line2}`,
            borderRadius: '8px',
            // 30px is the design's avatar size; below 768px it also has to be a
            // real tap target, so it grows to the 36px floor.
            width: isMobile ? '36px' : '30px',
            height: isMobile ? '36px' : '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: themeColors[theme].ink2,
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          {userInfo?.name?.[0] || userInfo?.email?.[0] || ''}
        </button>

        {/* Profile Dropdown Menu */}
        {isProfileOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            backgroundColor: themeColors[theme].background,
            border: `1px solid ${themeColors[theme].border}`,
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            minWidth: isMobile ? '200px' : '240px',
            zIndex: 50
          }}>
            <div style={{ 
              padding: '16px',
              borderBottom: `1px solid ${themeColors[theme].border}`
            }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                marginBottom: '4px'
              }}>
                {userInfo?.name}
              </div>
              <div style={{ 
                fontSize: '14px',
                color: themeColors[theme].secondaryText
              }}>
                {userInfo?.email}
              </div>
            </div>

            {/* Credits info - show for all screen sizes */}
            <div style={{ 
              padding: '16px',
              borderBottom: `1px solid ${themeColors[theme].border}`
            }}>
              {isTeamBilled(userInfo?.billing_source) ? (
                // A team wallet pays: the usable shared number is what matters,
                // and the second line names the two buckets it came from.
                <div style={{ fontSize: '14px' }} title={teamCreditsBreakdown(userInfo?.team)}>
                  <strong>
                    {teamUsableCredits(userInfo?.team).toLocaleString('id-ID')}
                  </strong>{' '}
                  kredit tim
                  <div style={{
                    fontSize: '12px',
                    color: themeColors[theme].secondaryText,
                    marginTop: '4px',
                  }}>
                    {teamCreditsBreakdown(userInfo?.team)}
                  </div>
                  {userInfo?.team?.name && (
                    <div style={{
                      fontSize: '12px',
                      color: themeColors[theme].secondaryText,
                      marginTop: '4px',
                    }}>
                      Tim {userInfo.team.name}
                    </div>
                  )}
                </div>
              ) : window.innerWidth <= 768 ? (
                // Mobile view - show full greeting
                <div style={{ fontSize: '14px' }}>
                  <strong>{userInfo?.credits_used}</strong> /{' '}
                  {userInfo?.billing_source === 'none'
                    ? 0
                    : userInfo?.billing_source === 'personal'
                      ? (userInfo?.credits_total ?? 10000)
                      : userInfo?.status === 'lite' ? 0 : userInfo?.status === 'active' || userInfo?.status === 'limited' ? 10000 : 0} kredit
                </div>
              ) : (
                // Desktop view - show simplified version
                <div style={{ fontSize: '14px' }}>
                  Kredit terpakai: <strong>{userInfo?.credits_used}</strong>
                </div>
              )}
            </div>

            {/* Team surface */}
            <button
              onClick={() => {
                setIsProfileOpen(false);
                navigate('/team');
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'none',
                border: 'none',
                borderBottom: `1px solid ${themeColors[theme].border}`,
                textAlign: 'left',
                cursor: 'pointer',
                color: themeColors[theme].text,
                fontSize: '14px',
              }}
            >
              Tim
            </button>

            {/* Top-up had no wording anywhere in the spine (P1-3): this lands
                straight on the credit packs section of the team panel. */}
            <button
              onClick={() => {
                setIsProfileOpen(false);
                navigate('/team#topup');
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'none',
                border: 'none',
                borderBottom: `1px solid ${themeColors[theme].border}`,
                textAlign: 'left',
                cursor: 'pointer',
                color: themeColors[theme].text,
                fontSize: '14px',
              }}
            >
              Tambah kredit
            </button>

            <button
              onClick={() => {
                setIsProfileOpen(false);
                setModelsModalOpen(true);
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'none',
                border: 'none',
                borderBottom: `1px solid ${themeColors[theme].border}`,
                textAlign: 'left',
                cursor: 'pointer',
                color: themeColors[theme].text,
                fontSize: '14px',
              }}
            >
              Model yang dipakai
            </button>

            <button
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              style={{
                width: '100%',
                padding: '16px',
                background: 'none',
                border: 'none',
                borderBottom: `1px solid ${themeColors[theme].border}`,
                textAlign: 'left',
                cursor: 'pointer',
                color: themeColors[theme].text
              }}
            >
              {theme === 'light' ? 'Mode gelap' : 'Mode terang'}
            </button>

            {/* Contact Support Button */}
            <a
              href="https://wa.me/6285601841025?text=Saya%20mau%20bertanya%20tentang%20PowerAI"
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '16px',
                background: 'none',
                border: 'none',
                borderBottom: `1px solid ${themeColors[theme].border}`,
                textAlign: 'left',
                cursor: 'pointer',
                color: theme === 'light' ? '#1A1A1A' : '#FFFFFF',
                textDecoration: 'none',
                fontSize: '14px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'light' ? '#F5F5F5' : '#2D2D2D';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2M12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 15 3.8 13.47 3.8 11.91C3.81 7.37 7.5 3.67 12.05 3.67M8.53 7.33C8.37 7.33 8.1 7.39 7.87 7.64C7.65 7.89 7 8.5 7 9.71C7 10.93 7.89 12.1 8 12.27C8.14 12.44 9.76 14.94 12.25 16C12.84 16.27 13.3 16.42 13.66 16.53C14.25 16.72 14.79 16.69 15.22 16.63C15.7 16.56 16.68 16.03 16.89 15.45C17.1 14.87 17.1 14.38 17.04 14.27C16.97 14.17 16.81 14.11 16.56 14C16.31 13.86 15.09 13.26 14.87 13.18C14.64 13.1 14.5 13.06 14.31 13.3C14.15 13.55 13.67 14.11 13.53 14.27C13.38 14.44 13.24 14.46 13 14.34C12.74 14.21 11.94 13.95 11 13.11C10.26 12.45 9.77 11.64 9.62 11.39C9.5 11.15 9.61 11 9.73 10.89C9.84 10.78 10 10.6 10.1 10.45C10.23 10.31 10.27 10.2 10.35 10.04C10.43 9.87 10.39 9.73 10.33 9.61C10.27 9.5 9.77 8.26 9.56 7.77C9.36 7.29 9.16 7.35 9 7.34C8.86 7.34 8.7 7.33 8.53 7.33Z" />
              </svg>
              Hubungi dukungan
            </a>

            <ExportDataButton theme={theme === 'light' ? 'light' : 'dark'} variant="menu" />

            <button
              onClick={() => {
                // Clear all localStorage items
                localStorage.clear();
                
                // Remove the authentication cookie with all possible variations
                Cookies.remove('powerai_auth', { 
                  domain: '.powerai.id',
                  secure: true,
                  sameSite: 'lax'
                });
                
                // Also try removing without domain specification
                Cookies.remove('powerai_auth');
                
                // Also try removing from root domain
                Cookies.remove('powerai_auth', { 
                  domain: 'powerai.id'
                });
                
                // Navigate to login page
                navigate('/login');
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                color: '#DC2626',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'light' ? '#FEE2E2' : '#7F1D1D';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Keluar
            </button>
          </div>
        )}
      </div>
    </div>
    <ModelsModal
      open={modelsModalOpen}
      onClose={() => setModelsModalOpen(false)}
      theme={theme}
      c={themeColors[theme]}
    />
    
    {/* Non-chat surfaces: the panel IS the centre column. Its own scroll
        container lives here (the panels deliberately own no chrome), so the
        sidebar and the top bar stay put while it scrolls. */}
    {!isChatSurface && (
      <div
        className="custom-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {panel}
      </div>
    )}

    {isChatSurface && (
    <>
    {isLoadingConversations ? (
      <div style={{
        flex: 1,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        backgroundColor: themeColors[theme].background,
      }}>
        {/* Loading message */}
        <div style={{
          textAlign: 'center',
          padding: '40px 0',
          color: themeColors[theme].secondaryText,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '15px',
        }}>
          {/* Loading spinner */}
          <svg 
            width="30" 
            height="30" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            style={{ animation: 'spin 1s linear infinite' }}
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <span style={{ fontSize: '16px' }}>Loading your conversations...</span>
        </div>

        {/* Skeleton messages */}
        {[1, 2, 3].map((n) => (
          <div key={n} style={{
            padding: '20px',
            borderBottom: `1px solid ${themeColors[theme].border}`,
            backgroundColor: n % 2 === 0 
              ? themeColors[theme].messageBackground.assistant 
              : themeColors[theme].messageBackground.user,
            animation: 'pulse 1.5s infinite',
            animationDelay: `${n * 0.2}s`,
          }}>
            <div style={{
              maxWidth: '800px',
              margin: '0 auto',
              display: 'flex',
              gap: '20px',
            }}>
              {/* Avatar skeleton */}
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                backgroundColor: theme === 'light' ? '#E0E0E0' : '#404040',
                animation: 'pulse 1.5s infinite',
              }} />
              
              {/* Message content skeleton */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                {/* Model name skeleton */}
                <div style={{
                  width: '100px',
                  height: '12px',
                  borderRadius: '4px',
                  backgroundColor: theme === 'light' ? '#E0E0E0' : '#404040',
                  animation: 'pulse 1.5s infinite',
                }} />
                
                {/* Message text skeletons */}
                {[1, 2, 3].map((line) => (
                  <div 
                    key={line}
                    style={{
                      height: '12px',
                      borderRadius: '4px',
                      backgroundColor: theme === 'light' ? '#E0E0E0' : '#404040',
                      width: `${Math.random() * 40 + 60}%`,
                      animation: 'pulse 1.5s infinite',
                      animationDelay: `${line * 0.1}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <>
        {userInfo?.status !== 'active' && userInfo?.status !== 'verified' && userInfo?.status !== 'lite' && userInfo?.status !== 'expired' && userInfo?.status !== 'limited' && showExpiredWarning && (
          <div style={{
            backgroundColor: theme === 'light' ? '#FEF3C7' : '#3D2F00',
            padding: isMobile ? '8px 12px' : '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '12px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            textAlign: 'center',
            position: 'sticky',
            top: isMobile ? '56px' : '64px',
            zIndex: 5,
            width: '100%'
          }}>
            <button
              onClick={() => setShowExpiredWarning(false)}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'light' ? '#92400E' : '#FCD34D',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <span style={{
              color: theme === 'light' ? '#92400E' : '#FCD34D',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1 0 100%' : 'initial',
              marginBottom: isMobile ? '4px' : '0',
              paddingRight: '24px'
            }}>
              Akun anda melewati batas kredit atau sudah habis masa berlaku 1 bulan
            </span>
            <button
              onClick={() => navigate('/subscription')}
              style={{
                padding: isMobile ? '4px 8px' : '4px 12px',
                background: theme === 'light' ? '#92400E' : '#FCD34D',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                color: theme === 'light' ? '#FCD34D' : '#92400E',
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Perpanjang Langganan
            </button>
          </div>
        )}
        {userInfo?.status === 'lite' && showLiteWarning && (
          <div style={{
            backgroundColor: theme === 'light' ? '#FEF3C7' : '#3D2F00',
            padding: isMobile ? '8px 12px' : '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '12px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            textAlign: 'center',
            position: 'sticky',
            top: isMobile ? '56px' : '64px',
            zIndex: 5,
            width: '100%'
          }}>
            <button
              onClick={() => setShowLiteWarning(false)}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'light' ? '#92400E' : '#FCD34D',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <span style={{
              color: theme === 'light' ? '#92400E' : '#FCD34D',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1 0 100%' : 'initial',
              marginBottom: isMobile ? '4px' : '0',
              paddingRight: '24px'
            }}>
              Anda menggunakan versi Lite. Upgrade ke PRO untuk mengakses semua model premium.
            </span>
            <button
              onClick={() => navigate('/subscription')}
              style={{
                padding: isMobile ? '4px 8px' : '4px 12px',
                background: theme === 'light' ? '#92400E' : '#FCD34D',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                color: theme === 'light' ? '#FCD34D' : '#92400E',
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Upgrade Sekarang
            </button>
          </div>
        )}
        {/* Expired Status Warning */}
        {userInfo?.status === 'expired' && showExpiredWarning && (
          <div style={{
            backgroundColor: theme === 'light' ? '#FEE2E2' : '#7F1D1D',
            padding: isMobile ? '8px 44px 8px 12px' : '8px 44px 8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '12px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            textAlign: 'center',
            position: 'sticky',
            top: isMobile ? '56px' : '64px',
            zIndex: 5,
            width: '100%'
          }}>
            <button
              aria-label="Tutup peringatan langganan"
              onClick={() => setShowExpiredWarning(false)}
              style={{
                position: 'absolute',
                right: '8px',
                top: '8px',
                minWidth: '36px',
                minHeight: '36px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'light' ? '#DC2626' : '#FCA5A5',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <span style={{
              color: theme === 'light' ? '#DC2626' : '#FCA5A5',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1 0 100%' : 'initial',
              marginBottom: isMobile ? '4px' : '0',
              paddingRight: '24px'
            }}>
              Langganan Anda telah berakhir. Perpanjang untuk melanjutkan.
            </span>
            <button
              onClick={() => navigate('/subscription')}
              style={{
                padding: isMobile ? '4px 8px' : '4px 12px',
                minHeight: '36px',
                background: theme === 'light' ? '#DC2626' : '#FCA5A5',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                color: theme === 'light' ? '#FFFFFF' : '#7F1D1D',
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Perpanjang langganan
            </button>
          </div>
        )}
        
        {/* Limited Status Warning */}
        {userInfo?.status !== 'expired' && (userInfo?.status === 'limited' || personalCreditsExhausted) && showLimitedWarning && (
          <div style={{
            backgroundColor: theme === 'light' ? '#FEF3C7' : '#3D2F00',
            padding: isMobile ? '8px 12px' : '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '12px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            textAlign: 'center',
            position: 'sticky',
            top: isMobile ? '56px' : '64px',
            zIndex: 5,
            width: '100%'
          }}>
            <button
              onClick={() => setShowLimitedWarning(false)}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'light' ? '#92400E' : '#FCD34D',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <span style={{
              color: theme === 'light' ? '#92400E' : '#FCD34D',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1 0 100%' : 'initial',
              marginBottom: isMobile ? '4px' : '0',
              paddingRight: '24px'
            }}>
              Kredit Anda sudah habis. Perpanjang langganan untuk melanjutkan.
            </span>
            <button
              onClick={() => navigate('/subscription')}
              style={{
                padding: isMobile ? '4px 8px' : '4px 12px',
                background: theme === 'light' ? '#92400E' : '#FCD34D',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                color: theme === 'light' ? '#FCD34D' : '#92400E',
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Upgrade Sekarang
            </button>
          </div>
        )}
        {userInfo?.status !== 'active' && userInfo?.status !== 'verified' && userInfo?.status !== 'lite' && userInfo?.status !== 'expired' && userInfo?.status !== 'limited' && showGenericWarning && (
          <div style={{
            backgroundColor: theme === 'light' ? '#FEF3C7' : '#3D2F00',
            padding: isMobile ? '8px 12px' : '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '12px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            textAlign: 'center',
            position: 'sticky',
            top: isMobile ? '56px' : '64px',
            zIndex: 5,
            width: '100%'
          }}>
            <button
              onClick={() => setShowGenericWarning(false)}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'light' ? '#92400E' : '#FCD34D',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <span style={{
              color: theme === 'light' ? '#92400E' : '#FCD34D',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1 0 100%' : 'initial',
              marginBottom: isMobile ? '4px' : '0',
              paddingRight: '24px'
            }}>
              Akun anda melewati batas kredit atau sudah habis masa berlaku 1 bulan
            </span>
            <button
              onClick={() => navigate('/subscription')}
              style={{
                padding: isMobile ? '4px 8px' : '4px 12px',
                background: theme === 'light' ? '#92400E' : '#FCD34D',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                color: theme === 'light' ? '#FCD34D' : '#92400E',
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Upgrade Sekarang
            </button>
          </div>
        )}
        {(() => {
          // Compose-mode chips add roughly one chip-row (~40px) on
          // top of the composer's normal height. Bump paddingBottom
          // (which keeps the last message above the fixed composer)
          // by that same amount when compare is active.
          const compareActive = !!(selectedModels && selectedModels.length > 1);
          const compareReserve = compareActive ? 40 : 0;
          return (
            <div
              style={{
                ...updatedLayoutStyles.chatContainer,
                // Mobile: the composer is a static footer BELOW this scroller
                // now, so the thread only needs breathing room, not clearance.
                paddingBottom: `${(isMobile ? 24 : 156) + compareReserve}px`,
                height: 'calc(100vh - 180px)',
                maxHeight: 'calc(100vh - 180px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                position: 'relative',
                marginBottom: '0',
              }}
              ref={chatContainerRef}
              className="custom-scrollbar"
            >
          {playbookRun?.status === 'started' && !readOnly && (
            <div style={{
              maxWidth: '700px',
              margin: '0 auto 8px',
              padding: isMobile ? '10px 12px' : '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              flexWrap: 'wrap',
              background: themeColors[theme].panel,
              border: `1px solid ${themeColors[theme].line}`,
              borderRadius: '12px',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: themeColors[theme].ink }}>
                  Playbook{playbookRun.playbook_name ? ` · ${playbookRun.playbook_name}` : ''}
                </div>
                <div style={{ fontSize: '12px', color: themeColors[theme].ink2, marginTop: '2px' }}>
                  {(messages || []).some((m) => (
                    m.role === 'assistant'
                    && m.isComplete !== false
                    && !(m.model || '').toLowerCase().includes('playbook')
                  ))
                    ? 'Kalau hasil Playbook sudah sesuai, tekan Tandai selesai. Boleh lanjut tanya di thread ini.'
                    : 'Ceritakan faktanya di kotak chat. Saya tanya dulu kalau ada yang kurang. Tandai selesai dipakai setelah hasilnya keluar.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onCompletePlaybook?.()}
                disabled={!!playbookCompletePending}
                className="pa-ghost"
                style={{
                  background: 'none',
                  border: `1px solid ${themeColors[theme].line2}`,
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: themeColors[theme].ink,
                  cursor: playbookCompletePending ? 'progress' : 'pointer',
                  fontFamily: 'inherit',
                  minHeight: '40px',
                  opacity: playbookCompletePending ? 0.55 : 1,
                }}
              >
                {playbookCompletePending ? 'Menyimpan…' : 'Tandai selesai'}
              </button>
            </div>
          )}
          {paginationData.hasPrevious && (
            <div style={{
              padding: '10px',
              display: 'flex',
              justifyContent: 'center',
              borderBottom: `1px solid ${themeColors[theme].border}`
            }}>
              <button
                onClick={() => fetchConversationMessages(
                  currentConversationId!,
                  conversations.find(c => c.id === currentConversationId)?.uuid || '',
                  paginationData.previousCursor,
                  'previous'
                )}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: `1px solid ${themeColors[theme].border}`,
                  backgroundColor: theme === 'light' ? '#FFFFFF' : '#2D2D2D',
                  color: themeColors[theme].text,
                  cursor: isLoadingMessages ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                disabled={isLoadingMessages}
              >
                {isLoadingMessages ? (
                  <>
                    <span style={{ 
                      width: '16px', 
                      height: '16px', 
                      border: '2px solid',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
          
          {isSwitchingConversation ? (
            // Loading skeleton for messages
            <div style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}>
              {[1, 2, 3].map((n) => (
                <div key={n} style={{
                  padding: '20px',
                  borderBottom: `1px solid ${themeColors[theme].border}`,
                  backgroundColor: n % 2 === 0 
                    ? themeColors[theme].messageBackground.assistant 
                    : themeColors[theme].messageBackground.user,
                  animation: 'pulse 1.5s infinite',
                  animationDelay: `${n * 0.2}s`,
                }}>
                  <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    display: 'flex',
                    gap: '20px',
                  }}>
                    {/* Avatar skeleton */}
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: theme === 'light' ? '#E0E0E0' : '#404040',
                      animation: 'pulse 1.5s infinite',
                    }} />
                    
                    {/* Message content skeleton */}
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      {[1, 2, 3].map((line) => (
                        <div 
                          key={line}
                          style={{
                            height: '12px',
                            borderRadius: '4px',
                            backgroundColor: theme === 'light' ? '#E0E0E0' : '#404040',
                            width: `${Math.random() * 40 + 60}%`,
                            animation: 'pulse 1.5s infinite',
                            animationDelay: `${line * 0.1}s`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Only show the full-screen loader when there is nothing rendered
                  yet - an already-visible thread must never blank to a spinner
                  (post-stream refetch flash on a new chat's first message). */}
              {isLoadingConversation && messages.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: themeColors[theme].secondaryText
                  }}>
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24"
                      style={{ 
                        animation: 'spin 1s linear infinite',
                      }}
                    >
                      <circle 
                        cx="12" 
                        cy="12" 
                        r="10" 
                        stroke="currentColor" 
                        strokeWidth="3" 
                        fill="none" 
                        strokeDasharray="30 50"
                      />
                    </svg>
                    <span>Loading conversation...</span>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div style={{
                  minHeight: 'calc(100% - 140px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px 20px',
                  gap: '12px',
                  maxWidth: '720px',
                  margin: '0 auto'
                }}>
                  {/* MOBILE: one quiet mark instead of the full welcome. Fully
                      empty read as a load error (2026-08-04 follow-up), the
                      full decoration read as clutter (2026-08-03) - this is
                      the middle: glyph + wordmark, nothing else. */}
                  {isMobile && (
                    <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                      <img
                        src={getModelIcon('auto', theme)}
                        alt="Power AI"
                        style={{ width: '34px', height: '34px', objectFit: 'contain', opacity: 0.8, marginBottom: '10px' }}
                      />
                      <div style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '11px',
                        letterSpacing: '0.26em',
                        color: themeColors[theme].mutedText,
                      }}>
                        POWER AI
                      </div>
                    </div>
                  )}

                  {/* Header. On MOBILE the welcome decoration (heading,
                      subtitle, credits chip, starter prompts below) stays
                      dropped (2026-08-03, Evan: "biar kosong aja"): the
                      composer's own placeholder already says what to do.
                      Functional pills (project context, team welcome) stay. */}
                  {!isMobile && (
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '18px'
                  }}>
                    <img
                      src={getModelIcon('auto', theme)}
                      alt="Power AI"
                      style={{ width: '40px', height: '40px', objectFit: 'contain', marginBottom: '16px', opacity: 0.9 }}
                    />
                    <h1 style={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: isMobile ? '28px' : '38px',
                      fontWeight: 650,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.12,
                      marginBottom: '10px',
                      marginTop: 0,
                      color: themeColors[theme].ink,
                      // Balance the wrap so the last line never drops a lone orphan word.
                      ['textWrap' as any]: 'balance',
                    }}>
                      {selectedModels && selectedModels.length > 1
                        ? `🔀 Compare mode: ${selectedModels.length} models siap jawab paralel`
                        : 'Selamat datang di Power AI'}
                    </h1>
                    {(!selectedModels || selectedModels.length <= 1) && (
                      <p style={{
                        fontSize: '15px',
                        color: themeColors[theme].secondaryText,
                        marginTop: '4px',
                        marginBottom: 0,
                        maxWidth: '480px',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        lineHeight: 1.6,
                      }}>
                        Tanya apa saja. Power AI otomatis memilih cara tercepat
                        menjawab, dan mencari web saat dibutuhkan.
                      </p>
                    )}
                    {selectedModels && selectedModels.length > 1 && (
                      <div style={{
                        fontSize: 12,
                        color: theme === 'light' ? '#737373' : '#A3A3A3',
                        marginTop: 2,
                      }}>
                        Klik kartu model di bawah untuk swap pilihan, atau langsung tulis prompt di bawah.
                      </div>
                    )}
                  </div>
                  )}

                  {/* Functional pills: rendered on EVERY size (the project
                      pill is stateful context), only the credits chip is
                      desktop-only decoration. */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                      marginTop: isMobile ? 0 : '14px',
                    }}>
                      {!isMobile && userInfo && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 12px',
                          borderRadius: '999px',
                          backgroundColor: themeColors[theme].surface2,
                          border: `1px solid ${themeColors[theme].border}`,
                          fontSize: '12px'
                        }}>
                          <CircleStackIcon width={14} height={14} color={themeColors[theme].mutedText} />
                          <span style={{
                            fontWeight: '500',
                            color: themeColors[theme].secondaryText
                          }}>
                            {userInfo.credits_used} kredit terpakai
                          </span>
                        </div>
                      )}

                      {/* "Chat baru di project ini": the context used to be
                          invisible on this screen and a reload dropped it
                          (P0-3). Now it is a pill you can see and remove. */}
                      {(() => {
                        const pendingProject = (projects || []).find(
                          (p) => p.id === pendingProjectId
                        );
                        if (!pendingProject) return null;
                        return (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '7px',
                            padding: '5px 8px 5px 12px',
                            borderRadius: '999px',
                            backgroundColor: themeColors[theme].jadeDim,
                            border: `1px solid ${themeColors[theme].accentBorder}`,
                            fontSize: '12px',
                            maxWidth: '280px',
                          }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={themeColors[theme].jade} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            </svg>
                            <span style={{
                              fontWeight: 500,
                              color: themeColors[theme].ink2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              Project · {pendingProject.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => onClearPendingProject?.()}
                              title="Chat tanpa project"
                              aria-label="Keluarkan chat baru ini dari project"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: themeColors[theme].ink3,
                                cursor: 'pointer',
                                fontSize: '15px',
                                lineHeight: 1,
                                padding: '0 2px',
                                flexShrink: 0,
                              }}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })()}
                    </div>

                  {/* Welcome to the team. Shown once, right where a freshly
                      joined member lands, because everything it explains
                      (privacy, who pays, where sharing lives) is otherwise only
                      discoverable by accident. Dismissing stamps a per-team
                      seen flag; it never comes back. */}
                  {teamWelcome && (
                    <div style={{
                      width: '100%',
                      maxWidth: '560px',
                      background: themeColors[theme].panel,
                      border: `1px solid ${themeColors[theme].jade}`,
                      borderRadius: '14px',
                      padding: '16px 18px',
                      marginBottom: '4px',
                      textAlign: 'left',
                    }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: 650,
                        color: themeColors[theme].ink,
                        letterSpacing: '-0.01em',
                      }}>
                        {teamWelcome.teamName
                          ? `Selamat datang di tim ${teamWelcome.teamName}!`
                          : 'Selamat datang di tim kamu!'}
                      </div>
                      <ul style={{
                        margin: '10px 0 0',
                        padding: '0 0 0 18px',
                        fontSize: '13px',
                        lineHeight: 1.7,
                        color: themeColors[theme].ink2,
                      }}>
                        <li>Chat kamu privat secara default, tagihannya ditanggung kredit tim.</li>
                        <li>Mau berbagi ke tim? Tekan Bagikan ke tim di atas chat.</li>
                        <li>Project dan chat tim ada di sidebar kiri.</li>
                      </ul>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            onDismissTeamWelcome?.();
                            navigate('/team');
                          }}
                          className="pa-send-btn"
                          style={{
                            height: '32px',
                            padding: '0 14px',
                            borderRadius: '9px',
                            border: 'none',
                            background: themeColors[theme].btnPrimaryBg,
                            color: themeColors[theme].btnPrimaryText,
                            fontSize: '12.5px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Lihat tim
                        </button>
                        <button
                          onClick={() => onDismissTeamWelcome?.()}
                          className="pa-ghost"
                          style={{
                            height: '32px',
                            padding: '0 14px',
                            borderRadius: '9px',
                            border: `1px solid ${themeColors[theme].line2}`,
                            background: 'transparent',
                            color: themeColors[theme].ink2,
                            fontSize: '12.5px',
                            fontWeight: 500,
                            cursor: 'pointer',
                          }}
                        >
                          Mulai chat
                        </button>
                      </div>
                    </div>
                  )}

                  {(myPrograms || []).length > 0 && !readOnly && (
                    <div style={{
                      width: '100%',
                      maxWidth: '560px',
                      marginTop: isMobile ? '4px' : '2px',
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: themeColors[theme].ink3,
                        marginBottom: '8px',
                      }}>
                        Misi kamu
                      </div>
                      {(myPrograms || []).slice(0, 2).map((row) => (
                        <button
                          key={row.uuid}
                          type="button"
                          onClick={() => navigate(`/team/programs/${row.uuid}`)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '14px 16px',
                            marginBottom: '10px',
                            borderRadius: '14px',
                            background: themeColors[theme].surface,
                            border: `1px solid ${themeColors[theme].accentBorder || themeColors[theme].border}`,
                            color: themeColors[theme].text,
                            fontFamily: "'Inter', system-ui, sans-serif",
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 600, color: themeColors[theme].ink }}>
                            {row.name}
                          </div>
                          <div style={{ fontSize: '12.5px', color: themeColors[theme].ink2, marginTop: '4px' }}>
                            {row.missions.filter((m) => m.my_status === 'completed').length} dari {row.missions.length} Misi selesai
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {hasTeam && !readOnly && (playbooks || []).length === 0 && (
                    <button
                      type="button"
                      onClick={() => navigate('/team/playbooks')}
                      style={{
                        display: 'block',
                        width: '100%',
                        maxWidth: '560px',
                        textAlign: 'left',
                        padding: '14px 16px',
                        marginTop: isMobile ? '4px' : '2px',
                        marginBottom: '10px',
                        borderRadius: '14px',
                        background: themeColors[theme].surface,
                        border: `1px solid ${themeColors[theme].border}`,
                        color: themeColors[theme].text,
                        fontFamily: "'Inter', system-ui, sans-serif",
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: themeColors[theme].ink3,
                        marginBottom: '6px',
                      }}>
                        Playbook
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: themeColors[theme].ink }}>
                        Pekerjaan berulang tim
                      </div>
                      <div style={{
                        fontSize: '12.5px',
                        color: themeColors[theme].ink2,
                        marginTop: '4px',
                        lineHeight: 1.45,
                      }}>
                        Misalnya Laporan kunjungan. Instruksinya sudah disetujui, anggota cukup mengisi input singkat.
                      </div>
                    </button>
                  )}

                  {hasTeam && !readOnly && (playbooks || []).length > 0 && (
                    <div style={{
                      width: '100%',
                      maxWidth: '560px',
                      marginTop: isMobile ? '4px' : '2px',
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginBottom: '8px',
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: themeColors[theme].ink3,
                        }}>
                          Coba ini
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate('/team/playbooks')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: themeColors[theme].ink2,
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            padding: 0,
                          }}
                        >
                          Semua Playbook
                        </button>
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: '10px',
                      }}>
                        {(playbooks || []).slice(0, 4).map((row) => (
                          <button
                            key={row.uuid}
                            type="button"
                            onClick={() => onStartPlaybook?.(row)}
                            style={{
                              textAlign: 'left',
                              padding: '14px 16px',
                              borderRadius: '14px',
                              background: themeColors[theme].surface,
                              border: `1px solid ${themeColors[theme].border}`,
                              color: themeColors[theme].text,
                              fontFamily: "'Inter', system-ui, sans-serif",
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontSize: '14px', fontWeight: 600, color: themeColors[theme].ink }}>
                              {row.name}
                            </div>
                            {row.description && (
                              <div style={{
                                fontSize: '12.5px',
                                color: themeColors[theme].ink2,
                                marginTop: '4px',
                                lineHeight: 1.45,
                              }}>
                                {row.description}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggestion chips - 2×2 grid of Indonesian starter prompts.
                      Clicking fills the composer (via setInputMessage) and focuses
                      it. Never auto-submits. Hidden in a read-only shared view,
                      where there is no composer to fill, and on MOBILE (the
                      clean-empty rule above). */}
                  {!readOnly && !isMobile && (!selectedModels || selectedModels.length <= 1) && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '10px',
                      width: '100%',
                      maxWidth: '560px',
                    }}>
                      {[
                        // Thin stroke line icons per the Obsidian mockup aesthetic
                        { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></svg>, text: 'Cari berita teknologi terbaru hari ini' },
                        { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21a9 9 0 1 1 9-9c0 2-1.5 3-3 3h-2a2 2 0 0 0-2 2c0 .6.2 1 .5 1.4.3.5.5.9.5 1.6a1.9 1.9 0 0 1-2 2z" /><circle cx="7.5" cy="11.5" r=".8" /><circle cx="10.5" cy="7.5" r=".8" /><circle cx="15" cy="8" r=".8" /></svg>, text: 'Buatkan gambar logo untuk bisnis kopi' },
                        { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3v18h18" /><path d="M8 16v-5M13 16V8M18 16v-3" /></svg>, text: 'Jelaskan cara membuat laporan keuangan sederhana' },
                        { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>, text: 'Bantu tulis email profesional ke klien' },
                      ].map((chip) => (
                        <button
                          key={chip.text}
                          onClick={() => {
                            setInputMessage(chip.text);
                            requestAnimationFrame(() => {
                              const el = textareaRef?.current;
                              if (el) {
                                el.focus();
                                el.style.height = 'auto';
                                el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
                                el.setSelectionRange(chip.text.length, chip.text.length);
                              }
                            });
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '11px',
                            textAlign: 'left',
                            padding: '14px 16px',
                            borderRadius: '14px',
                            background: themeColors[theme].surface,
                            border: `1px solid ${themeColors[theme].border}`,
                            color: themeColors[theme].text,
                            fontFamily: "'Inter', system-ui, sans-serif",
                            fontSize: '14px',
                            lineHeight: 1.35,
                            cursor: 'pointer',
                            transition: 'border-color 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = themeColors[theme].borderStrong;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = themeColors[theme].border;
                          }}
                        >
                          <span style={{ flexShrink: 0, display: 'grid', placeItems: 'center', color: themeColors[theme].ink3 }}>{chip.icon}</span>
                          <span>{chip.text}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Model card grid removed - selection is 'auto' only. Kept in-tree
                      (never rendered) to avoid a large deletion. */}
                  {false && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '8px',
                    width: '100%',
                    padding: '0 8px'
                  }}>
                    {modelCategories.map((category) => (
                      <div key={category.category}>
                        {/* Category Header */}
                        <h2 style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: theme === 'light' ? '#666' : '#888',
                          marginBottom: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {category.category}
                        </h2>

                        {/* Models in Category */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                          gap: '6px',
                          marginBottom: '12px'
                        }}>
                          {category.models.map((model) => (
                            <React.Fragment key={model.value}>
                              <button
                                onClick={() => {
                                  if (userInfo?.status === 'lite' && !isModelLite(model.value)) {
                                    return; // Prevent click for Pro-only models on Lite plan
                                  }
                                  handleModelSelect(model.value as Model);
                                  // Focus the bottom textarea and make cursor active
                                  const bottomTextarea = document.querySelector('textarea');
                                  if (bottomTextarea) {
                                    bottomTextarea.focus();
                                    // Force cursor to be visible and active
                                    bottomTextarea.style.caretColor = theme === 'light' ? '#000000' : '#FFFFFF';
                                    bottomTextarea.style.cursor = 'text';
                                    // Set cursor position to end
                                    const length = bottomTextarea.value.length;
                                    bottomTextarea.setSelectionRange(length, length);
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                  padding: '8px',
                                  backgroundColor: 
                                    userInfo?.status === 'expired'
                                      ? theme === 'light'
                                        ? '#FEE2E2'
                                        : '#7F1D1D'
                                      : (userInfo?.status === 'limited' && !isModelFree(model.value)) || (userInfo?.status === 'lite' && !isModelLite(model.value))
                                        ? theme === 'light'
                                          ? '#F3F4F6'
                                          : '#2D2D2D'
                                        : (selectedModels ? selectedModels.includes(model.value as Model) : model.value === selectedModel)
                                          ? theme === 'light'
                                            ? 'rgba(16, 163, 127, 0.1)'
                                            : 'rgba(16, 163, 127, 0.2)'
                                          : model.value === 'power-ai-chosen-model' 
                                            ? theme === 'light'
                                              ? 'linear-gradient(135deg, #FEF6E4 0%, #FDF2D0 100%)'
                                              : 'linear-gradient(135deg, #2A2000 0%, #483800 100%)'
                                            : theme === 'light' ? '#FFFFFF' : '#1E1E1E',
                                  border: 
                                    userInfo?.status === 'expired'
                                      ? `2px solid ${theme === 'light' ? '#DC2626' : '#FCA5A5'}`
                                      : (userInfo?.status === 'limited' && !isModelFree(model.value)) || (userInfo?.status === 'lite' && !isModelLite(model.value))
                                        ? `1px solid ${theme === 'light' ? '#E5E7EB' : '#374151'}`
                                        : (selectedModels ? selectedModels.includes(model.value as Model) : model.value === selectedModel)
                                          ? `1px solid #34D399`
                                          : model.value === 'power-ai-chosen-model'
                                            ? `1px solid ${theme === 'light' ? '#FFB800' : '#FFD700'}`
                                            : `1px solid ${themeColors[theme].border}`,
                                  borderRadius: '6px',
                                  cursor: 
                                    userInfo?.status === 'expired' || 
                                    (userInfo?.status === 'limited' && !isModelFree(model.value)) || 
                                    (userInfo?.status === 'lite' && !isModelLite(model.value)) 
                                      ? 'not-allowed' 
                                      : 'pointer',
                                  transition: 'all 0.2s ease',
                                  textAlign: 'left',
                                  boxShadow: 
                                    userInfo?.status === 'expired' || 
                                    (userInfo?.status === 'limited' && !isModelFree(model.value)) || 
                                    (userInfo?.status === 'lite' && !isModelLite(model.value))
                                      ? 'none'
                                      : (selectedModels ? selectedModels.includes(model.value as Model) : model.value === selectedModel)
                                        ? `0 2px 8px rgba(16, 163, 127, 0.2)`
                                        : model.value === 'power-ai-chosen-model'
                                          ? `0 2px 8px ${theme === 'light' ? 'rgba(255, 184, 0, 0.2)' : 'rgba(255, 215, 0, 0.1)'}` 
                                          : '0 1px 2px rgba(0, 0, 0, 0.05)',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  opacity: 
                                    userInfo?.status === 'expired' || 
                                    (userInfo?.status === 'limited' && !isModelFree(model.value)) || 
                                    (userInfo?.status === 'lite' && !isModelLite(model.value)) 
                                      ? 0.6 
                                      : 1,
                                  filter: 
                                    userInfo?.status === 'expired' || 
                                    (userInfo?.status === 'limited' && !isModelFree(model.value)) || 
                                    (userInfo?.status === 'lite' && !isModelLite(model.value)) 
                                      ? 'grayscale(0.5)' 
                                      : 'none'
                                }}
                                onMouseEnter={(e) => {
                                  const isRestricted = 
                                    userInfo?.status === 'expired' || 
                                    (userInfo?.status === 'limited' && !isModelFree(model.value)) || 
                                    (userInfo?.status === 'lite' && !isModelLite(model.value));
                                  
                                  if (isRestricted) {
                                    return; // Prevent hover effect for restricted models
                                  }
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = (selectedModels ? selectedModels.includes(model.value as Model) : model.value === selectedModel)
                                    ? '0 4px 12px rgba(16, 163, 127, 0.3)'
                                    : model.value === 'power-ai-chosen-model'
                                      ? theme === 'light'
                                        ? '0 4px 12px rgba(255, 184, 0, 0.3)'
                                        : '0 4px 12px rgba(255, 215, 0, 0.2)'
                                      : theme === 'light'
                                        ? '0 2px 8px rgba(0, 0, 0, 0.08)'
                                        : '0 2px 8px rgba(0, 0, 0, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  const isRestricted = 
                                    userInfo?.status === 'expired' || 
                                    (userInfo?.status === 'limited' && !isModelFree(model.value)) || 
                                    (userInfo?.status === 'lite' && !isModelLite(model.value));
                                  
                                  if (isRestricted) {
                                    return; // Prevent hover effect for restricted models
                                  }
                                  e.currentTarget.style.transform = 'none';
                                  e.currentTarget.style.boxShadow = (selectedModels ? selectedModels.includes(model.value as Model) : model.value === selectedModel)
                                    ? `0 2px 8px rgba(16, 163, 127, 0.2)`
                                    : model.value === 'power-ai-chosen-model'
                                      ? `0 2px 8px ${theme === 'light' ? 'rgba(255, 184, 0, 0.2)' : 'rgba(255, 215, 0, 0.1)'}` 
                                      : '0 1px 2px rgba(0, 0, 0, 0.05)';
                                }}
                              >
                                {/* Status badges */}
                                {userInfo?.status === 'expired' && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    background: theme === 'light' ? '#DC2626' : '#7F1D1D',
                                    color: theme === 'light' ? '#FFFFFF' : '#FCA5A5',
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderBottomLeftRadius: '4px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    zIndex: 1
                                  }}>
                                    🚫 EXPIRED
                                  </div>
                                )}
                                {userInfo?.status === 'limited' && !isModelFree(model.value) && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    background: theme === 'light' ? '#F59E0B' : '#92400E',
                                    color: theme === 'light' ? '#FFFFFF' : '#FCD34D',
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderBottomLeftRadius: '4px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    zIndex: 1
                                  }}>
                                    ⚠️ LIMITED
                                  </div>
                                )}
                                {userInfo?.status === 'lite' && !isModelLite(model.value) && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    background: theme === 'light' ? '#1F2937' : '#111827',
                                    color: theme === 'light' ? '#F3F4F6' : '#E5E7EB',
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderBottomLeftRadius: '4px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    zIndex: 1
                                  }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                    Premium
                                  </div>
                                )}
                                {(selectedModels ? selectedModels.includes(model.value as Model) : model.value === selectedModel) && userInfo?.status !== 'expired' && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    background: '#34D399',
                                    color: '#FFFFFF',
                                    fontSize: '9px',
                                    padding: '2px 6px',
                                    borderBottomLeftRadius: '4px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 3,
                                  }}>
                                    {selectedModels && selectedModels.length > 1 && (
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 12, height: 12,
                                        borderRadius: 2,
                                        background: '#FFFFFF',
                                        color: '#34D399',
                                        fontSize: 10,
                                        fontWeight: 800,
                                        lineHeight: 1,
                                      }}>✓</span>
                                    )}
                                    {selectedModels && selectedModels.length > 1 ? `${selectedModels.indexOf(model.value as Model) + 1}/${selectedModels.length}` : 'Selected'}
                                  </div>
                                )}
                                {model.value === 'power-ai-chosen-model' && userInfo?.status !== 'expired' && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    background: theme === 'light' ? '#FFB800' : '#FFD700',
                                    color: theme === 'light' ? '#000000' : '#000000',
                                    fontSize: '9px',
                                    padding: '2px 6px',
                                    borderBottomLeftRadius: '4px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase'
                                  }}>
                                    Recommended
                                  </div>
                                )}
                                {/* Model Header */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <img 
                                    src={getModelIcon(model.value, theme)} 
                                    alt={model.label}
                                    style={{ 
                                      width: model.value === 'power-ai-chosen-model' ? '24px' : '20px',
                                      height: model.value === 'power-ai-chosen-model' ? '24px' : '20px',
                                      objectFit: 'contain'
                                    }}
                                  />
                                  <div style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <div style={{
                                      fontSize: model.value === 'power-ai-chosen-model' ? '14px' : '13px',
                                      fontWeight: model.value === 'power-ai-chosen-model' ? '600' : '500',
                                      color: model.value === 'power-ai-chosen-model'
                                        ? theme === 'light' ? '#B45309' : '#FFD700'
                                        : themeColors[theme].text,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '2px'
                                    }}>
                                      {model.label}
                                      {model.isThinking && (
                                        <span 
                                          title="Thinking/Reasoning Model"
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '3px',
                                            backgroundColor: theme === 'light' ? '#F3E8FF' : '#581C87',
                                            fontSize: '10px',
                                            marginLeft: '2px',
                                            flexShrink: 0
                                          }}
                                        >
                                          🧠
                                        </span>
                                      )}
                                      {model.isVision && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '2px', verticalAlign: 'middle', color: theme === 'light' ? '#666' : '#888' }}>
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                          <circle cx="8.5" cy="8.5" r="1.5" />
                                          <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Features/Capabilities */}
                                <div style={{
                                  fontSize: '11px',
                                  color: themeColors[theme].secondaryText,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '3px',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    gap: '3px',
                                    flexWrap: 'wrap'
                                  }}>
                                    {isFileUploadEnabled(model.value) && (
                                      <span style={{
                                        padding: '1px 4px',
                                        borderRadius: '4px',
                                        backgroundColor: model.value === 'power-ai-chosen-model'
                                          ? theme === 'light' ? 'rgba(255, 184, 0, 0.2)' : 'rgba(255, 215, 0, 0.2)'
                                          : theme === 'light' ? '#F3F4F6' : '#2D2D2D',
                                        fontSize: '10px'
                                      }}>
                                        📎
                                      </span>
                                    )}
                                    {shouldShowBrowserIcon(model.value) && (
                                      <span style={{
                                        padding: '1px 4px',
                                        borderRadius: '4px',
                                        backgroundColor: model.value === 'power-ai-chosen-model'
                                          ? theme === 'light' ? 'rgba(255, 184, 0, 0.2)' : 'rgba(255, 215, 0, 0.2)'
                                          : theme === 'light' ? '#F3F4F6' : '#2D2D2D',
                                        fontSize: '10px'
                                      }}>
                                        🌐
                                      </span>
                                    )}
                                    {(
                                      model.value === 'gpt-image-1' ||
                                      model.value === 'gpt-image-2' ||
                                      model.value === 'powerai-image-1' ||
                                      model.value === 'black-forest-labs/FLUX.1-kontext-max' ||
                                      model.value === 'black-forest-labs/FLUX.2-pro' ||
                                      model.value === 'google/gemini-3.1-flash-image-preview'
                                    ) && (
                                      <span style={{ fontSize: '13px', color: theme === 'light' ? '#666' : '#888', marginLeft: '2px', verticalAlign: 'middle' }}>🖼️</span>
                                    )}
                                    <span style={{
                                      padding: '1px 4px',
                                      borderRadius: '4px',
                                      backgroundColor: model.value === 'power-ai-chosen-model'
                                        ? theme === 'light' ? 'rgba(255, 184, 0, 0.2)' : 'rgba(255, 215, 0, 0.2)'
                                        : isModelFree(model.value)
                                          ? theme === 'light' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.2)'
                                          : theme === 'light' ? '#F3F4F6' : '#2D2D2D',
                                      fontSize: '10px',
                                      color: model.value === 'power-ai-chosen-model'
                                        ? theme === 'light' ? '#B45309' : '#FFD700'
                                        : isModelFree(model.value)
                                          ? '#34D399'
                                          : theme === 'light' ? '#6B7280' : '#9CA3AF',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '2px'
                                    }}>
                                      {model.credits}
                                      <CircleStackIcon style={{ 
                                        color: model.value === 'power-ai-chosen-model'
                                          ? theme === 'light' ? '#B45309' : '#FFD700'
                                          : isModelFree(model.value) ? '#34D399' : '#DAA520',
                                        width: '10px',
                                        height: '10px'
                                      }} />
                                    </span>
                                  </div>
                                </div>
                                {model.value === 'power-ai-chosen-model' && (
                                  <div style={{
                                    fontSize: '10px',
                                    color: theme === 'light' ? '#B45309' : '#FFD700',
                                    marginTop: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                    Smart Model Selection
                                  </div>
                                )}
                                {/* Show explanation only for Power AI model */}
                                {model.value === 'power-ai-chosen-model' && !isMobile && (
                                  <div style={{
                                    gridColumn: '1 / -1', // Make it span full width
                                    textAlign: 'left',
                                    marginTop: '8px',
                                    padding: '16px',
                                    backgroundColor: theme === 'light' ? 'rgba(255, 184, 0, 0.1)' : 'rgba(255, 215, 0, 0.1)',
                                    borderRadius: '8px',
                                    border: `1px solid ${theme === 'light' ? 'rgba(255, 184, 0, 0.2)' : 'rgba(255, 215, 0, 0.2)'}`,
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '12px',
                                    }}>
                                      <div style={{
                                        backgroundColor: theme === 'light' ? '#FFB800' : '#FFD700',
                                        borderRadius: '50%',
                                        padding: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                      }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
                                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                        </svg>
                                      </div>
                                      <div>
                                        <div style={{
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          color: theme === 'light' ? '#B45309' : '#FFD700',
                                          marginBottom: '8px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}>
                                          Smart Model Selection
                                        </div>
                                        <div style={{
                                          fontSize: '13px',
                                          color: theme === 'light' ? '#92400E' : '#FFE55C',
                                          lineHeight: '1.5',
                                        }}>
                                          Power AI uses advanced analysis to automatically select the most suitable AI model for your specific query. Our system:
                                          <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'auto 1fr',
                                            gap: '8px',
                                            marginTop: '8px'
                                          }}>
                                            {[
                                              {
                                                icon: (
                                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"/>
                                                    <path d="M12 16v-4"/>
                                                    <path d="M12 8h.01"/>
                                                  </svg>
                                                ),
                                                text: "Analyzes your query's complexity and requirements"
                                              },
                                              {
                                                icon: (
                                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                                                  </svg>
                                                ),
                                                text: "Identifies whether you need code, analysis, creativity, or factual responses"
                                              },
                                              {
                                                icon: (
                                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                                  </svg>
                                                ),
                                                text: "Chooses the best-performing model for your specific needs"
                                              },
                                              {
                                                icon: (
                                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                                    <polyline points="22 4 12 14.01 9 11.01"/>
                                                  </svg>
                                                ),
                                                text: "Optimizes for both quality and efficiency"
                                              }
                                            ].map((item, index) => (
                                              <React.Fragment key={index}>
                                                <div style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  color: theme === 'light' ? '#92400E' : '#FFE55C'
                                                }}>
                                                  {item.icon}
                                                </div>
                                                <div style={{
                                                  fontSize: '12px',
                                                  color: theme === 'light' ? '#92400E' : '#FFE55C'
                                                }}>
                                                  {item.text}
                                                </div>
                                              </React.Fragment>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </button>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              ) : (
                // Existing messages display
                <>
                  {(() => {
                    // Group consecutive assistant messages with distinct models
                    // into a single compare-turn unit; render those as a
                    // horizontal card grid instead of stacked bubbles.
                    type Unit =
                      | { kind: 'single'; message: MessageWithCitations; idx: number }
                      | { kind: 'compare'; messages: MessageWithCitations[]; idx: number };
                    const units: Unit[] = [];
                    let i = 0;
                    while (i < messages.length) {
                      const m = messages[i];
                      if (m.role === 'assistant') {
                        const group: MessageWithCitations[] = [m];
                        const seenModels = new Set<string>([m.model]);
                        let j = i + 1;
                        while (
                          j < messages.length &&
                          messages[j].role === 'assistant' &&
                          messages[j].model &&
                          !seenModels.has(messages[j].model)
                        ) {
                          group.push(messages[j]);
                          seenModels.add(messages[j].model);
                          j++;
                        }
                        if (group.length > 1) {
                          units.push({ kind: 'compare', messages: group, idx: i });
                          i = j;
                          continue;
                        }
                      }
                      units.push({ kind: 'single', message: m, idx: i });
                      i++;
                    }
                    return units.map((unit) => {
                      if (unit.kind === 'compare') {
                        const allDone = unit.messages.every((m) => m.isComplete !== false);
                        return (
                          <div key={`cmp-${unit.idx}`} style={{
                            padding: '20px',
                            ...messageStyles.assistant,
                          }}>
                            <div style={{
                              maxWidth: '1280px',
                              margin: '0 auto',
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontSize: 11,
                                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: theme === 'light' ? '#737373' : '#A3A3A3',
                                marginBottom: 12,
                                paddingLeft: 4,
                              }}>
                                <span>🔀</span>
                                <span>Compare · {unit.messages.length} models</span>
                                {!allDone && (
                                  <span style={{
                                    fontSize: 10,
                                    color: '#34D399',
                                    fontWeight: 600,
                                  }}>● streaming</span>
                                )}
                              </div>
                              <div style={{
                                display: 'grid',
                                gap: 12,
                                gridTemplateColumns: isMobile
                                  ? '1fr'
                                  : `repeat(${unit.messages.length}, minmax(0, 1fr))`,
                              }}>
                                {unit.messages.map((m, mi) => {
                                  const isThisDone = m.isComplete !== false;
                                  return (
                                    <div
                                      key={mi}
                                      style={{
                                        background: theme === 'light' ? '#FFFFFF' : '#1E1E1E',
                                        border: `1px solid ${theme === 'light' ? '#E5E7EB' : '#3A3A3A'}`,
                                        borderRadius: 12,
                                        padding: 14,
                                        minHeight: 220,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        ...(m.error && {
                                          background: theme === 'light' ? '#FEF2F2' : '#3B1818',
                                          borderColor: theme === 'light' ? '#FCA5A5' : '#991B1B',
                                          color: theme === 'light' ? '#991B1B' : '#FCA5A5',
                                        }),
                                      }}
                                    >
                                      <div style={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: 'hidden',
                                      }}>
                                        {renderMessage(m)}
                                      </div>
                                      {isThisDone && !m.error && setSelectedModels && (
                                        <div style={{
                                          marginTop: 10,
                                          paddingTop: 10,
                                          borderTop: `1px solid ${theme === 'light' ? '#F0EDE6' : '#2D2D2D'}`,
                                          display: 'flex',
                                          justifyContent: 'flex-end',
                                        }}>
                                          <button
                                            onClick={() => {
                                              setSelectedModels([m.model as Model]);
                                              setSelectedModel(m.model as Model);
                                            }}
                                            style={{
                                              background: 'transparent',
                                              border: `1px solid ${theme === 'light' ? '#D4D4D4' : '#525252'}`,
                                              color: theme === 'light' ? '#0A0A0A' : '#FFFFFF',
                                              padding: '4px 10px',
                                              borderRadius: 999,
                                              fontSize: 11,
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 4,
                                              transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.background =
                                                theme === 'light' ? '#F8F6F1' : '#2D2D2D';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.background = 'transparent';
                                            }}
                                            title={`Lanjut chat hanya pakai ${(modelCategories.flatMap(c => c.models).find(mm => mm.value === m.model)?.label || m.model)}`}
                                          >
                                            Continue with {(modelCategories.flatMap(c => c.models).find(mm => mm.value === m.model)?.label || m.model)} →
                                          </button>
                                        </div>
                                      )}
                                      {!isThisDone && (
                                        <div style={{
                                          marginTop: 10,
                                          paddingTop: 10,
                                          borderTop: `1px solid ${theme === 'light' ? '#F0EDE6' : '#2D2D2D'}`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 6,
                                          fontSize: 11,
                                          color: theme === 'light' ? '#737373' : '#A3A3A3',
                                          fontFamily: 'ui-monospace, monospace',
                                        }}>
                                          <span style={{
                                            display: 'inline-block',
                                            width: 6, height: 6,
                                            borderRadius: '50%',
                                            background: '#34D399',
                                            animation: 'pulse 1.4s ease-in-out infinite',
                                          }} />
                                          streaming...
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      const message = unit.message;
                      const isUser = message.role === 'user';
                      return (
                        <div key={unit.idx} className="pa-message-in" style={{
                          padding: isUser ? '10px 22px' : '14px 22px',
                        }}>
                          <div style={{
                            maxWidth: '700px',
                            margin: '0 auto',
                            display: 'flex',
                            justifyContent: isUser ? 'flex-end' : 'flex-start',
                          }}>
                            {isUser ? (
                              <div style={{
                                maxWidth: '76%',
                                background: message.error
                                  ? (theme === 'light' ? '#FEF2F2' : '#3B1818')
                                  : themeColors[theme].panel2,
                                border: `1px solid ${message.error
                                  ? (theme === 'light' ? '#FCA5A5' : '#991B1B')
                                  : themeColors[theme].line}`,
                                color: message.error
                                  ? (theme === 'light' ? '#991B1B' : '#FCA5A5')
                                  : themeColors[theme].ink,
                                borderRadius: '14px 14px 4px 14px',
                                padding: '11px 15px',
                                fontSize: '14.5px',
                              }}>
                                {renderMessage(message)}
                              </div>
                            ) : (
                              <div style={{
                                width: '100%',
                                ...messageStyles.content,
                              }}>
                                {renderMessage(message)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                  <div ref={messagesEndRef} />
                </>
              )}
            </>
          )}
        </div>
          );
        })()}
      </>
    )}

    {/* WELCOME-SCREEN composer only. With messages in the thread Chat.tsx
        mounts ChatInput on the same fixed slot; before the 2026-08-03 mobile
        review BOTH rendered at once (two textareas, doubled fade gradient).
        Read-only shared views get the slim "Dibagikan oleh ..." bar instead. */}
    {!readOnly && messages.length === 0 && (
    <div style={{
      // MOBILE (2026-08-04): static footer of the shell column, same reasoning
      // as ChatInput - the shell shrinks with the keyboard, so the composer
      // rises in LAYOUT and Safari has nothing to pan. Desktop keeps the
      // fixed overlay with the rail/panel offsets.
      ...(isMobile
        ? { position: 'static' as const, width: '100%' }
        : {
            position: 'fixed' as const,
            bottom: 0,
            left: isSidebarOpen ? '280px' : '52px',
            right: showRightPanel ? '320px' : '0',
          }),
      background: theme === 'dark'
        ? 'linear-gradient(to top, #0A0B0D 62%, rgba(10,11,13,0))'
        : 'linear-gradient(to top, #F7F8FA 62%, rgba(247,248,250,0))',
      padding: '14px 22px 12px',
      paddingBottom: keyboardInset > 0 ? '10px' : 'calc(12px + env(safe-area-inset-bottom))',
      transition: 'left 0.3s ease, right 0.3s ease',
      zIndex: 10
    }}>
      <div style={{
        position: 'relative',
        maxWidth: '700px',
        margin: '0 auto'
      }}>
        <div
        className="pa-composer"
        style={{
          position: 'relative',
          background: themeColors[theme].composerBg,
          border: isDragging
            ? `1px dashed ${themeColors[theme].line2}`
            : `1px solid ${themeColors[theme].line2}`,
          borderRadius: '14px',
          padding: '13px',
          boxShadow: themeColors[theme].composerShadow,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          ['--pa-focus-ring' as any]: themeColors[theme].jade,
          ['--pa-focus-ring-dim' as any]: themeColors[theme].jadeDim,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        >
        {selectedModels && selectedModels.length > 1 && setSelectedModels && (() => {
          const total = selectedModels.reduce((sum, mv) => {
            const m = modelCategories.flatMap((c) => c.models).find((mm) => mm.value === mv);
            const c = (m as any)?.credits;
            return sum + (typeof c === 'number' ? c : 0);
          }, 0);
          const hasUnlimitedOnly = total === 0;
          const removeModel = (mv: string) => {
            if (selectedModels.length <= 1) return;
            setSelectedModels(selectedModels.filter((v) => v !== mv));
          };
          return (
            <div style={{
              marginBottom: 10,
              paddingBottom: 10,
              borderBottom: `1px solid ${theme === 'light' ? '#F0EDE6' : '#2D2D2D'}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
            }}>
              {/* Compact compare-mode chip with cost */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 9px',
                borderRadius: 999,
                background: theme === 'light' ? '#EEF2FF' : '#312E81',
                border: `1px solid ${theme === 'light' ? '#C7D2FE' : '#4338CA'}`,
                fontSize: 11,
                color: theme === 'light' ? '#4338CA' : '#C7D2FE',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontWeight: 600,
              }}>
                <span>🔀</span>
                <span>{selectedModels.length} models</span>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>{hasUnlimitedOnly ? 'free' : `~${total} credits`}</span>
              </div>
              {/* Selected model chips with remove */}
              {selectedModels.map((mv) => {
                const meta = modelCategories.flatMap((c) => c.models).find((mm) => mm.value === mv);
                if (!meta) return null;
                return (
                  <div
                    key={mv}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '3px 6px 3px 5px',
                      borderRadius: 999,
                      background: theme === 'light' ? '#F8F6F1' : '#2A2A2A',
                      border: `1px solid ${theme === 'light' ? '#E5E7EB' : '#3A3A3A'}`,
                      fontSize: 11,
                      color: theme === 'light' ? '#1F1F1F' : '#FFFFFF',
                    }}
                  >
                    <img src={getModelIcon(meta.value, theme)} alt={meta.label} style={{ width: 12, height: 12 }} />
                    <span>{meta.label}</span>
                    <button
                      onClick={() => removeModel(mv)}
                      title="Remove from compare"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: theme === 'light' ? '#737373' : '#A3A3A3',
                        cursor: 'pointer',
                        padding: 0,
                        lineHeight: 1,
                        fontSize: 13,
                        marginLeft: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })()}
        {/* Konteks workspace untuk chat baru (pilihan pending dari panel File)
            atau percakapan kosong yang sudah punya file aktif. */}
        <ContextStrip
          conversationId={currentConversationId}
          projectId={currentConversationId != null ? currentProjectId : pendingProjectId}
          theme={theme}
          themeColors={themeColors}
        />
        {/* Attachment chips */}
        {isFileUploadEnabled(selectedModel) && selectedFiles && selectedFiles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
            {selectedFiles.map((fileData, index) => (
              <div key={fileData.filename} style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 6px 3px 10px',
                background: themeColors[theme].panel2,
                border: `1px solid ${themeColors[theme].line}`,
                borderRadius: '999px', fontSize: '12px', color: themeColors[theme].ink2,
              }}>
                {truncateFilename(fileData.file.name, 15)}
                <button
                  onClick={() => {
                    if (setSelectedFiles) {
                      const newFiles = selectedFiles.filter((_, i) => i !== index);
                      setSelectedFiles(newFiles);
                      if (newFiles.length === 0) { setSelectedFile(null); setSelectedFileName(null); }
                    }
                  }}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', color: themeColors[theme].ink3 }}
                >×</button>
              </div>
            ))}
          </div>
        )}

        {/* Inline upload error (workspace) - mirrors the File panel's error red */}
        {uploadError && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '6px',
            marginBottom: '8px', fontSize: '12px', lineHeight: 1.4,
            color: 'rgba(229,71,77,0.9)',
          }}>
            <span style={{ flex: 1, minWidth: 0 }}>{uploadError}</span>
            {onDismissUploadError && (
              <button
                onClick={onDismissUploadError}
                aria-label="Tutup pesan error"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', color: 'rgba(229,71,77,0.9)', lineHeight: 1 }}
              >×</button>
            )}
          </div>
        )}

        <input type="file" id="file-upload-empty" style={{ display: 'none' }} onChange={handleFileSelect} multiple accept="*/*" />

        <textarea
          ref={textareaRef}
          value={inputMessage}
          onChange={(e) => { setInputMessage(e.target.value); handleTextAreaHeight(e); }}
          onKeyPress={handleKeyPress}
          className="pa-composer-input"
          style={{
            display: 'block',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: themeColors[theme].ink,
            fontSize: '14.5px',
            lineHeight: '1.5',
            resize: 'none',
            minHeight: '24px',
            maxHeight: '150px',
            width: '100%',
            padding: '1px 4px 13px',
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
          placeholder={
            selectedModels && selectedModels.length > 1
              ? `Tanya satu, ${selectedModels.length} AI bakal jawab paralel...`
              : 'Tanya apa saja…'
          }
          rows={1}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            {isFileUploadEnabled(selectedModel) && (
              <>
                <label htmlFor="file-upload-empty" className="pa-icon-ghost" title="Lampirkan file"
                  style={{ width: '29px', height: '29px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: themeColors[theme].ink3, cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21.4 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.2-9.19a4 4 0 015.65 5.66l-9.2 9.19a2 2 0 01-2.82-2.83l8.49-8.48"/></svg>
                </label>
                <label htmlFor="file-upload-empty" className="pa-icon-ghost" title="Lampirkan gambar"
                  style={{ width: '29px', height: '29px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: themeColors[theme].ink3, cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                </label>
              </>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onOpenVoice && (
              <button type="button" onClick={onOpenVoice} className="pa-icon-ghost" title="Obrolan suara (live)"
                aria-label="Obrolan suara"
                style={{ width: '29px', height: '29px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: themeColors[theme].ink3, cursor: 'pointer', background: 'transparent', border: 'none', padding: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14v-3a8 8 0 0 1 16 0v3"/><path d="M3 14h3a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Z"/><path d="M18 14h3a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Z"/></svg>
              </button>
            )}
            {selectedModels && selectedModels.length > 1 ? (
            <button
              onClick={handleSubmit}
              className="pa-send-btn"
              disabled={!inputMessage.trim() || isLoading}
              style={{
                background: themeColors[theme].btnPrimaryBg,
                color: themeColors[theme].btnPrimaryText,
                border: 'none',
                borderRadius: '9px',
                padding: '8px 14px',
                cursor: (!inputMessage.trim() || isLoading) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                opacity: (!inputMessage.trim() || isLoading) ? 0.5 : 1,
                transition: 'background 0.15s ease',
                ['--pa-btn-hover' as any]: themeColors[theme].btnPrimaryHover,
              }}
            >
              {isLoading ? 'Mengirim…' : `Compare ${selectedModels.length} →`}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="pa-send-btn"
              aria-label="Send message"
              title="Kirim"
              disabled={!canSend}
              style={{
                width: '32px', height: '32px', minWidth: '32px', borderRadius: '9px',
                background: !canSend ? themeColors[theme].panel2 : themeColors[theme].btnPrimaryBg,
                color: !canSend ? themeColors[theme].ink3 : themeColors[theme].btnPrimaryText,
                border: !canSend ? `1px solid ${themeColors[theme].line}` : 'none',
                cursor: !canSend ? 'not-allowed' : 'pointer',
                display: 'grid', placeItems: 'center', flexShrink: 0,
                transition: 'background 0.15s ease',
                ['--pa-btn-hover' as any]: themeColors[theme].btnPrimaryHover,
              }}
            >
              {isLoading ? (
                <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="28 50" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19V5" />
                  <path d="m5 12 7-7 7 7" />
                </svg>
              )}
            </button>
          )}
          </div>
        </div>
        </div>
        {/* Branding caption: desktop only (on mobile it read as a dead gap
            between the composer and the keyboard). */}
        {!isMobile && (
          <div style={{
            textAlign: 'center',
            fontFamily: themeColors[theme].mono,
            fontSize: '10.5px',
            letterSpacing: '0.02em',
            color: themeColors[theme].ink3,
            marginTop: '9px',
          }}>
            POWER AI · AUTO SEARCH &amp; IMAGE
          </div>
        )}
      </div>
    </div>
    )}

    {/* Read-only shared conversation: same geometry / z-index as the composer,
        so the thread's bottom clearance is unchanged. Owner line on the left,
        the one write action a viewer HAS on the right. */}
    {readOnly && (
      <div style={{
        position: 'fixed',
        bottom: 0,
        // 52px = the collapsed rail, which owns that strip on desktop.
      left: isSidebarOpen ? '280px' : (isMobile ? '0' : '52px'),
        right: showRightPanel ? '320px' : '0',
        background: theme === 'dark'
          ? 'linear-gradient(to top, #0A0B0D 62%, rgba(10,11,13,0))'
          : 'linear-gradient(to top, #F7F8FA 62%, rgba(247,248,250,0))',
        padding: '14px 22px 12px',
        transition: 'left 0.3s ease, right 0.3s ease',
        zIndex: 10,
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: themeColors[theme].composerBg,
            border: `1px solid ${themeColors[theme].line2}`,
            borderRadius: '14px',
            padding: '11px 13px',
            boxShadow: themeColors[theme].composerShadow,
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 500,
                color: themeColors[theme].ink2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                Dibagikan oleh {sharedOwnerEmail || 'anggota tim'}
              </div>
              {duplicateError ? (
                <div style={{ fontSize: '11.5px', color: 'rgba(229,71,77,0.9)', marginTop: '3px' }}>
                  {duplicateError}
                </div>
              ) : (
                <div style={{ fontSize: '11.5px', color: themeColors[theme].ink3, marginTop: '3px' }}>
                  Mode baca saja. Duplikat dulu untuk melanjutkan sendiri.
                </div>
              )}
            </div>
            <button
              onClick={handleDuplicate}
              className="pa-send-btn"
              disabled={duplicating || !conversationUuid}
              title="Salin percakapan ini ke chat saya"
              style={{
                flexShrink: 0,
                background: themeColors[theme].btnPrimaryBg,
                color: themeColors[theme].btnPrimaryText,
                border: 'none',
                borderRadius: '9px',
                padding: isMobile ? '8px 11px' : '9px 14px',
                fontSize: isMobile ? '12px' : '13px',
                fontWeight: 600,
                cursor: (duplicating || !conversationUuid) ? 'not-allowed' : 'pointer',
                opacity: (duplicating || !conversationUuid) ? 0.5 : 1,
                transition: 'background 0.15s ease',
                ['--pa-btn-hover' as any]: themeColors[theme].btnPrimaryHover,
              }}
            >
              {duplicating ? 'Menduplikat…' : (isMobile ? 'Duplikat' : 'Duplikat ke chat saya')}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
    )}
  </div>
  );
}, (prevProps, nextProps) => {
  // Consider content "heavy" if total characters exceed 10000 (about 1000 words)
  const HEAVY_CONTENT_THRESHOLD = 10000;
  
  // Calculate total characters only if messages have changed
  const shouldOptimize = calculateTotalCharacters(nextProps.messages) > HEAVY_CONTENT_THRESHOLD;

  return shouldOptimize ? (
    // Apply strict comparison only for heavy content. Any prop the user
    // can toggle from inside the navbar/profile/composer must be listed
    // here, otherwise heavy chats will visibly stop responding to
    // clicks (e.g. profile dropdown stuck closed because isProfileOpen
    // change was treated as "equal" and re-render was skipped).
    prevProps.messages === nextProps.messages &&
    prevProps.theme === nextProps.theme &&
    prevProps.isLoadingMessages === nextProps.isLoadingMessages &&
    prevProps.isSwitchingConversation === nextProps.isSwitchingConversation &&
    prevProps.selectedModel === nextProps.selectedModel &&
    prevProps.selectedModels === nextProps.selectedModels &&
    prevProps.isSidebarOpen === nextProps.isSidebarOpen &&
    prevProps.showRightPanel === nextProps.showRightPanel &&
    prevProps.isProfileOpen === nextProps.isProfileOpen &&
    prevProps.userInfo === nextProps.userInfo &&
    prevProps.isLoadingConversations === nextProps.isLoadingConversations &&
    prevProps.inputMessage === nextProps.inputMessage &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.selectedFiles === nextProps.selectedFiles &&
    prevProps.uploadError === nextProps.uploadError &&
    prevProps.onCreditRedeemed === nextProps.onCreditRedeemed &&
    prevProps.currentConversationId === nextProps.currentConversationId &&
    // Teams phase 2: without these a heavy thread would keep its composer after
    // opening a shared conversation, and the share pill would never flip.
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.conversationUuid === nextProps.conversationUuid &&
    prevProps.sharedOwnerEmail === nextProps.sharedOwnerEmail &&
    prevProps.conversationTitle === nextProps.conversationTitle &&
    prevProps.branchedFrom === nextProps.branchedFrom &&
    prevProps.canShare === nextProps.canShare &&
    prevProps.canLinkShare === nextProps.canLinkShare &&
    prevProps.linkShareActive === nextProps.linkShareActive &&
    prevProps.shareVisibility === nextProps.shareVisibility &&
    prevProps.sharePending === nextProps.sharePending &&
    prevProps.shareReady === nextProps.shareReady &&
    // Teams phase 3: same reasoning for the project pill - without these a
    // heavy thread would never show the project it was just moved into.
    prevProps.canAssignProject === nextProps.canAssignProject &&
    prevProps.projects === nextProps.projects &&
    prevProps.currentProjectId === nextProps.currentProjectId &&
    prevProps.projectPending === nextProps.projectPending &&
    prevProps.teamWelcome === nextProps.teamWelcome &&
    prevProps.hasTeam === nextProps.hasTeam &&
    prevProps.playbooks === nextProps.playbooks &&
    prevProps.myPrograms === nextProps.myPrograms &&
    prevProps.playbookRun === nextProps.playbookRun &&
    prevProps.playbookCompletePending === nextProps.playbookCompletePending &&
    // Nav rework: the surface decides whether the centre column is the thread
    // or a panel, so a heavy chat would otherwise stay on screen after opening
    // /team. `panel` itself is a node and cannot be compared: panelProjectId is
    // the scalar that catches /p/1 -> /p/2, and panelTitle catches a rename.
    prevProps.surface === nextProps.surface &&
    prevProps.panelProjectId === nextProps.panelProjectId &&
    prevProps.panelTitle === nextProps.panelTitle &&
    // The back chevron's label follows the surface we popped from, so a heavy
    // thread would otherwise keep saying "Kembali ke chat" on the way out of a
    // project that was opened from /team.
    prevProps.backLabel === nextProps.backLabel &&
    prevProps.projectNeedsShareConfirm === nextProps.projectNeedsShareConfirm &&
    prevProps.pendingProjectId === nextProps.pendingProjectId
  ) : (
    // Always re-render for light content
    false
  );
});

export default MainContent;
